// react
import {
	ChangeEvent,
	ReactNode,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';

// router
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

// i18n
import { useTranslation } from 'react-i18next';

// context
import { ContextLoans, ContextProject, ContextUsersPermissions } from '../Project/Project.context';
import { ContextSpendPlan } from '../ProjectV2/ProjectV2.context';
import { ContextBanner, ContextUser } from '../../../App.context';

// hooks
import { useVoucher } from '../../../hooks/useVouchers';

// components
import { CardDetail } from '../../cards/CardDetail/CardDetail';
import { CardInvoiceDetails } from '../../cards/CardInvoiceDetails/CardInvoiceDetails';
import { DataRequestFieldRow } from '../../ui/DataRequestFieldRow/DataRequestFieldRow';
import { HeaderDetailPage } from '../../ui/HeaderDetailPage/HeaderDetailPage';
import { OnChainStatusBadge } from '../../ui/OnChainStatusBadge/OnChainStatusBadge';
import { PaymentTimeline } from '../../ui/PaymentTimeline/PaymentTimeline';
import { UserStamp } from '../../ui/UserStamp/UserStamp';
import { PageDetailSkeleton } from '../../ui/PageDetailSkeleton/PageDetailSkeleton';
import { VoucherPaymentReconciliation } from '../../ui/VoucherPaymentReconciliation/VoucherPaymentReconciliation';
import { BlockchainProcessing } from '../../ui/BlockchainProcessing/BlockchainProcessing';

// mui
import {
	alpha,
	Box,
	Button,
	Chip,
	CircularProgress,
	Collapse,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	IconButton,
	LinearProgress,
	Grid,
	MenuItem,
	Paper,
	Stack,
	TextField,
	Tooltip,
	Typography,
	useTheme,
} from '@mui/material';

// icons
import {
	AccountTree as IconActivity,
	AttachFile as IconDocument,
	Cancel as IconCancel,
	CheckCircle as IconCheckCircle,
	Close as IconClose,
	CallSplit as IconCallSplit,
	ConfirmationNumber as IconVoucher,
	Description as IconContract,
	EditNote as IconEditNote,
	ExpandMore as IconExpandMore,
	Group as IconGroup,
	History as IconHistory,
	HourglassEmpty as IconPending,
	Info as IconInfo,
	Link as IconLink,
	Assignment as IconWorkPlan,
	OpenInNew as IconOpenInNew,
	PlaylistAddCheck as IconChecklist,
	QrCode2 as IconQrCode,
	ReportProblem as IconException,
	Token as IconToken,
	ThumbDown as IconReject,
	ThumbUp as IconApprove,
	Verified as IconVerified,
} from '@mui/icons-material';

// utils
import { formatCurrency } from '../../../utils/currency';
import { getProjectReportingCurrency, restateViaCapturedRates } from '../../../utils/localCurrency';
import { resolveContractFxRate } from '../../../utils/fxRate';
import { getVoucherTaxWithheld } from '../../../utils/voucher';
import {
	getContractAmountForCurrency,
	getContractAmountsByCurrency,
	getContractNum,
	getContractSupplierName,
} from '../ProjectV2/Contracts/Contracts.utils';

// sse
import { useV2StatusListener } from '../../../utils/sse/useV2StatusListener';

// types
import {
	Activity,
	DataRequestField,
	SectionResponse,
	User,
	VerificationSection,
} from '../../../@types';

// dialogs
import { DialogAuditTimeline } from '../../dialogs/DialogAuditTimeline/DialogAuditTimeline';
import {
	DialogDocumentViewer,
	ViewerDocumentInfo,
} from '../../dialogs/DialogDocumentViewer/DialogDocumentViewer';
import { DialogEntityDocuments } from '../../dialogs/DialogEntityDocuments/DialogEntityDocuments';
import { DialogMarkPaymentComplete } from '../../dialogs/DialogMarkPaymentComplete/DialogMarkPaymentComplete';

// ui
import { BtnSplit } from '../../ui/BtnSplit/BtnSplit';
import { VoucherStatusChip } from '../../ui/VoucherStatusChip/VoucherStatusChip';

// services
import { fetchAiDocuments, uploadAiDocument } from '../../../services/aiApi';
import { updateVoucher as updateVoucherService } from '../../../services/api';

// data
import { COLORS, GRADIENTS } from '../../../data';

export const PageVoucher = () => {
	// hooks
	const { t } = useTranslation();
	const navigate = useNavigate();
	const theme = useTheme();
	const { voucherId } = useParams<{ voucherId: string }>();
	const [searchParams, setSearchParams] = useSearchParams();
	const voucherIdNumber = Number(voucherId);

	// context
	const { project } = useContext(ContextProject);
	const { loans: projectLoans } = useContext(ContextLoans);
	const { user: currentUser } = useContext(ContextUser);
	const { setBanner } = useContext(ContextBanner);
	const { usersPermissions: projectUsers } = useContext(ContextUsersPermissions);
	const {
		activities,
		conditions,
		contracts,
		workPlans,
		getExceptionByConditionId,
		getInvoicesByContract,
		refetchVouchers,
		vouchers,
	} = useContext(ContextSpendPlan);

	// Fetch voucher by id
	const {
		data: voucherData,
		loading: voucherLoading,
		refetch,
		update: updateVoucher,
	} = useVoucher({
		id: voucherIdNumber,
		isEnabled: Number.isFinite(voucherIdNumber),
		query: { populate: 'condition,contract,activity,recipientEntity' },
	});

	// Live updates: refetch this voucher when someone else changes its status.
	useV2StatusListener({ resource: 'voucher', id: voucherIdNumber, onChange: refetch });

	// State for rejection dialog
	const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
	const [rejectionReason, setRejectionReason] = useState('');
	const [actionLoading, setActionLoading] = useState(false);
	// Approving a voucher mints its NFT on-chain; drives the minting animation.
	const [isMinting, setIsMinting] = useState(false);

	// Mark-paid dialog (manual payment confirmation for vouchers paid off-hub)
	const [markPaidOpen, setMarkPaidOpen] = useState(false);

	// View-documents dialog (proof-of-payment + any other voucher-linked docs)
	const [documentsDialogOpen, setDocumentsDialogOpen] = useState(false);

	// State for section rejection dialog
	const [sectionRejectDialogOpen, setSectionRejectDialogOpen] = useState(false);
	const [sectionRejectId, setSectionRejectId] = useState<string | null>(null);
	const [sectionRejectReason, setSectionRejectReason] = useState('');
	const [sectionActionLoading, setSectionActionLoading] = useState<string | null>(null);

	// Inline field editing state for rejected sections
	// Key: `${sectionId}::${fieldId}` → edited value
	const [editingFieldValues, setEditingFieldValues] = useState<Record<string, string>>({});
	// Track which document fields are currently uploading: sectionId::fieldId → true
	const [fieldUploading, setFieldUploading] = useState<Record<string, boolean>>({});
	// Track newly-uploaded docs so UI reflects them before save: sectionId::fieldId → {id, name}
	const [fieldUploadedDocs, setFieldUploadedDocs] = useState<
		Record<string, { id: string; name: string }>
	>({});
	// Track existing docs that have been removed (before save): sectionId::fieldId → true
	const [removedFieldDocs, setRemovedFieldDocs] = useState<Record<string, boolean>>({});
	// Track which section is being saved
	const [fieldSaveLoading, setFieldSaveLoading] = useState<string | null>(null);
	// Track rejected sections that have been resubmitted (saved with new data)
	const [resubmittedSections, setResubmittedSections] = useState<Record<string, boolean>>({});

	// Whether the split-payment-group banner is expanded.
	const [splitGroupExpanded, setSplitGroupExpanded] = useState(false);

	// Track dialog state for refetch on close
	const dialogParam = searchParams.get('dialog');
	const prevDialogRef = useRef<string | null>(dialogParam);

	// Refetch data when dialog closes (dialog param goes from value to null)
	useEffect(() => {
		if (prevDialogRef.current && !dialogParam) {
			// Dialog was open and is now closed - refetch data
			refetch();
		}
		prevDialogRef.current = dialogParam;
	}, [dialogParam, refetch]);

	// Voucher lifecycle audit rows from public.activity. The voucher table
	// no longer carries approved_by / approved_at / redeemed_by / redeemed_at
	// columns (story 104) — those facts live on activity rows now, one per
	// status transition (VOUCHER_CREATED, VOUCHER_APPROVED, VOUCHER_REDEEMED,
	// VOUCHER_REJECTED). Pull the latest row for each type and feed actor +
	// timestamp into PaymentTimeline.
	const [voucherActivities, setVoucherActivities] = useState<Activity[]>([]);
	// Audit log dialog visibility is controlled by the `audit=1` URL param so
	// the dialog state survives reloads / sharing the link.
	const auditTimelineOpen = searchParams.get('audit') === '1';
	const setAuditTimelineOpen = (open: boolean) => {
		setSearchParams(
			(prev) => {
				const next = new URLSearchParams(prev);
				if (open) next.set('audit', '1');
				else next.delete('audit');
				return next;
			},
			{ replace: true }
		);
	};
	const [auditLoading, setAuditLoading] = useState(false);

	const fetchVoucherActivities = useCallback(async () => {
		if (!Number.isFinite(voucherIdNumber) || !project?.id) return;
		setAuditLoading(true);
		try {
			const accessToken = sessionStorage.getItem('accessToken');
			const idToken = sessionStorage.getItem('idToken');
			const res = await fetch(
				`${process.env.REACT_APP_API_URL}/activities?project=${project.id}&voucher=${voucherIdNumber}&populate=actor&order=DESC&orderBy=created&limit=50&page=1`,
				{
					headers: {
						Authorization: `Bearer ${accessToken}`,
						User: String(idToken),
					},
				}
			);
			if (res.ok) {
				const json = await res.json();
				setVoucherActivities(json.data || []);
			}
		} catch (err) {
			console.error('Failed to fetch voucher activities:', err);
		} finally {
			setAuditLoading(false);
		}
	}, [voucherIdNumber, project?.id]);

	useEffect(() => {
		fetchVoucherActivities();
	}, [fetchVoucherActivities, voucherData?.status]);

	/** Re-run the on-chain voucher write (mint or redeem) when the original
	 *  call did not land. The backend splits the lifecycle into two granular
	 *  legs and only retries whichever is missing; pass `step` from the
	 *  per-step retry button in the badge popover. Both legs are idempotent
	 *  via the voucher's `referenceId` (`vch-{voucherId}`) so re-issuing the
	 *  same call after the chain already settled is safe. */
	const handleResyncVoucherBlockchain = useCallback(
		async (step?: 'mint' | 'redeem') => {
			if (!Number.isFinite(voucherIdNumber)) return;
			try {
				const accessToken = sessionStorage.getItem('accessToken');
				const idToken = sessionStorage.getItem('idToken');
				const res = await fetch(
					`${process.env.REACT_APP_API_URL?.replace(/\/v1\/?$/, '')}/v2/vouchers/${voucherIdNumber}`,
					{
						method: 'PATCH',
						headers: {
							Authorization: `Bearer ${accessToken}`,
							User: String(idToken),
							'Content-Type': 'application/json',
						},
						body: JSON.stringify({ syncBlockchain: true, ...(step ? { step } : {}) }),
					}
				);
				if (!res.ok) {
					const err = await res.json().catch(() => ({}));
					setBanner({
						message: err?.error?.message || 'Failed to re-sync voucher to blockchain.',
						severity: 'error',
					});
					return;
				}
				setBanner({
					message: step
						? `Re-synced voucher ${step} on chain.`
						: 'Re-synced voucher to blockchain.',
					severity: 'success',
				});
				await refetch();
			} catch (err: any) {
				setBanner({
					message: err?.message || 'Failed to re-sync voucher to blockchain.',
					severity: 'error',
				});
			}
		},
		[voucherIdNumber, setBanner, refetch]
	);

	// Resolve the latest actor + timestamp for each lifecycle step.
	const lifecycle = useMemo(() => {
		const latest: Record<string, { actor: Partial<User> | null; created: string }> = {};
		// Activities are fetched DESC; iterate reverse so latest of each type wins.
		for (const a of [...voucherActivities].reverse()) {
			const rawActor = (a as { actor?: number | Partial<User> }).actor;
			const actor: Partial<User> | null =
				rawActor && typeof rawActor === 'object'
					? (rawActor as Partial<User>)
					: typeof rawActor === 'number'
						? projectUsers.find((u) => u.id === rawActor) ||
							(currentUser?.id === rawActor ? currentUser : null)
						: null;
			latest[a.type] = { actor, created: a.created };
		}
		return latest;
	}, [voucherActivities, projectUsers, currentUser]);

	// Helper to extract ID from potentially populated field
	const getId = <T extends { id: number }>(field: number | T | null | undefined): number | null => {
		if (field === null || field === undefined) return null;
		if (typeof field === 'number') return field;
		return field.id;
	};

	// Find voucher and related entities
	const { voucher, condition, contract, activity, workPlan, exception, supplierName } =
		useMemo(() => {
			if (!Number.isFinite(voucherIdNumber))
				return {
					activity: null,
					condition: null,
					contract: null,
					workPlan: null,
					exception: null,
					supplierName: 'Unknown',
					voucher: null,
				};

			const v = voucherData || vouchers.find((voucher) => voucher.id === voucherIdNumber);
			if (!v)
				return {
					activity: null,
					condition: null,
					contract: null,
					workPlan: null,
					exception: null,
					supplierName: 'Unknown',
					voucher: null,
				};

			const conditionId = getId(v.condition);
			const contractId = getId(v.contract);
			const activityId = getId(v.activity);

			const cond = conditionId ? conditions.find((c) => c.id === conditionId) : null;
			const cont = contractId ? contracts.find((c) => c.id === contractId) : null;
			const act = activityId
				? activities.find((a) => a.id === activityId)
				: cont
					? activities.find((a) => a.id === cont.planningActivity)
					: null;
			const env = cont
				? workPlans.find((e) => e.id === cont.workPlan)
				: cond
					? workPlans.find((e) => e.id === getId(cond.workPlan))
					: null;

			// Resolve supplier name from multiple sources
			let supplier = '';
			// 1. Check recipientEntity on the voucher
			if (v.recipientEntity) {
				if (typeof v.recipientEntity === 'object' && v.recipientEntity.name) {
					supplier = v.recipientEntity.name;
				}
			}
			// 2. Check contract entities
			if (!supplier && cont) {
				const contractSupplier = getContractSupplierName(cont);
				if (contractSupplier && contractSupplier !== 'Unknown Supplier') {
					supplier = contractSupplier;
				}
			}
			// 3. Default fallback
			if (!supplier) {
				supplier = t('spendPlan.supplier.unknown', 'Unknown Supplier');
			}

			const exc = cond ? getExceptionByConditionId(cond.id) : null;

			return {
				activity: act,
				condition: cond,
				contract: cont,
				workPlan: env,
				exception: exc,
				supplierName: supplier,
				voucher: v,
			};
		}, [
			activities,
			conditions,
			contracts,
			workPlans,
			getExceptionByConditionId,
			voucherData,
			voucherIdNumber,
			vouchers,
			t,
		]);

	// =========================================================================
	// Sibling vouchers — when a payment is split across earmarks each split
	// gets its own voucher, but they share the same verification work. We
	// treat the (condition, contract) group as a single review unit so the
	// reviewer doesn't have to verify or approve every split independently.
	// =========================================================================
	const siblingVouchers = useMemo(() => {
		if (!voucher) return [] as typeof vouchers;
		const condId = getId(voucher.condition);
		const contractId = getId(voucher.contract);
		if (!condId) return [];
		return vouchers.filter((v) => {
			if (v.id === voucher.id) return false;
			if (v.status === 'CANCELLED') return false;
			if (getId(v.condition) !== condId) return false;
			// Match on contract too when this voucher has one (avoids merging
			// vouchers that happen to share a base condition across contracts).
			if (contractId && getId(v.contract) !== contractId) return false;
			return true;
		});
	}, [voucher, vouchers]);

	/**
	 * Apply a transform to one section across the current voucher AND every
	 * sibling voucher (matched by sectionId). Each voucher carries its own
	 * `sectionResponses` array but the section IDs match because the splits
	 * were cloned from the same condition.
	 */
	const applySectionUpdateToSiblings = async (
		sectionId: string,
		mutator: (sr: SectionResponse) => SectionResponse
	) => {
		const accessToken = sessionStorage.getItem('accessToken') || '';
		const idToken = sessionStorage.getItem('idToken') || '';
		await Promise.all(
			siblingVouchers.map((sib) => {
				const updated = (sib.sectionResponses || []).map((sr) =>
					sr.sectionId === sectionId ? mutator(sr) : sr
				);
				return updateVoucherService({ accessToken, idToken }, sib.id, {
					sectionResponses: updated,
				});
			})
		);
	};

	/** Apply a status (and optional rejection reason) to every sibling voucher. */
	const applyStatusToSiblings = async (
		status: 'READY' | 'CANCELLED',
		extra?: { rejectionReason?: string }
	) => {
		const accessToken = sessionStorage.getItem('accessToken') || '';
		const idToken = sessionStorage.getItem('idToken') || '';
		await Promise.all(
			siblingVouchers.map((sib) =>
				updateVoucherService({ accessToken, idToken }, sib.id, { status, ...extra })
			)
		);
	};

	// handlers
	const handleOpenActivityPage = (activityId: number) => {
		navigate(`/project/${project?.idWbg}/spend-plans/activity/${activityId}`);
	};

	const handleOpenContractPage = (contractId: number) => {
		navigate(`/project/${project?.idWbg}/spend-plans/contract/${contractId}`);
	};

	const handleOpenWorkPlanPage = (workPlanId: number) => {
		navigate(`/project/${project?.idWbg}/spend-plans/work-planning/work-plan/${workPlanId}`);
	};

	const handleOverride = () => {
		if (!voucher) return;
		const params = new URLSearchParams(searchParams);
		params.set('dialog', 'overrideVoucher');
		params.set('dialogVoucher', String(voucher.id));
		setSearchParams(params);
	};

	// Approve voucher for payment
	const handleApprovePayment = async () => {
		if (!voucher) return;
		setActionLoading(true);
		setIsMinting(true);
		try {
			const res = await updateVoucher({ status: 'READY' });
			if (res.error) {
				setBanner({ message: res.error.message, severity: 'error' });
			} else {
				// Approve every sibling voucher in the same payment unit too —
				// the reviewer signs off on the group, not each split.
				if (siblingVouchers.length > 0) {
					await applyStatusToSiblings('READY');
				}
				await Promise.all([refetch(), refetchVouchers()]);
			}
		} catch (error) {
			console.error('Failed to approve voucher:', error);
			setBanner({ message: 'Failed to approve voucher. Please try again.', severity: 'error' });
		} finally {
			setActionLoading(false);
			setIsMinting(false);
		}
	};

	// Open rejection dialog
	const handleOpenRejectDialog = () => {
		setRejectDialogOpen(true);
		setRejectionReason('');
	};

	// Close rejection dialog
	const handleCloseRejectDialog = () => {
		setRejectDialogOpen(false);
		setRejectionReason('');
	};

	// Reject voucher
	const handleRejectPayment = async () => {
		if (!voucher || !rejectionReason.trim()) return;
		setActionLoading(true);
		try {
			const reason = rejectionReason.trim();
			const res = await updateVoucher({
				status: 'CANCELLED',
				rejectionReason: reason,
			});
			if (res.error) {
				setBanner({ message: res.error.message, severity: 'error' });
			} else {
				// Reject every sibling voucher in the same payment unit so the
				// whole group is cancelled together.
				if (siblingVouchers.length > 0) {
					await applyStatusToSiblings('CANCELLED', { rejectionReason: reason });
				}
				handleCloseRejectDialog();
				await Promise.all([refetch(), refetchVouchers()]);
			}
		} catch (error) {
			console.error('Failed to reject voucher:', error);
			setBanner({ message: 'Failed to reject voucher. Please try again.', severity: 'error' });
		} finally {
			setActionLoading(false);
		}
	};

	// Verify a single section (with revision tracking)
	const handleVerifySection = async (sectionId: string) => {
		if (!voucher?.sectionResponses || !currentUser) return;
		setSectionActionLoading(sectionId);
		try {
			const now = new Date().toISOString();
			const updated = voucher.sectionResponses.map((sr) => {
				if (sr.sectionId !== sectionId) return sr;
				// If this section was previously REJECTED, archive the rejection in revisionHistory
				const history = [...(sr.revisionHistory || [])];
				if (sr.status === 'REJECTED' && sr.rejectedBy && sr.rejectedAt) {
					history.push({
						rejectedBy: sr.rejectedBy,
						rejectedAt: sr.rejectedAt,
						rejectionReason: sr.rejectionReason,
						resolvedBy: currentUser.id,
						resolvedAt: now,
						resolution: 'VERIFIED' as const,
						fieldResponseSnapshot: sr.fieldResponses ? [...sr.fieldResponses] : null,
					});
				}
				return {
					...sr,
					status: 'VERIFIED' as const,
					verifiedBy: currentUser.id,
					verifiedAt: now,
					// Clear rejection fields on verify (history preserves them)
					rejectedBy: null,
					rejectedAt: null,
					rejectionReason: null,
					revisionHistory: history.length > 0 ? history : undefined,
				};
			});
			await updateVoucher({ sectionResponses: updated });
			// Mirror the verification onto every sibling voucher in the same
			// payment unit so the reviewer signs off once for the group.
			if (siblingVouchers.length > 0) {
				await applySectionUpdateToSiblings(sectionId, (sr) => {
					const history = [...(sr.revisionHistory || [])];
					if (sr.status === 'REJECTED' && sr.rejectedBy && sr.rejectedAt) {
						history.push({
							rejectedBy: sr.rejectedBy,
							rejectedAt: sr.rejectedAt,
							rejectionReason: sr.rejectionReason,
							resolvedBy: currentUser.id,
							resolvedAt: now,
							resolution: 'VERIFIED' as const,
							fieldResponseSnapshot: sr.fieldResponses ? [...sr.fieldResponses] : null,
						});
					}
					return {
						...sr,
						status: 'VERIFIED' as const,
						verifiedBy: currentUser.id,
						verifiedAt: now,
						rejectedBy: null,
						rejectedAt: null,
						rejectionReason: null,
						revisionHistory: history.length > 0 ? history : undefined,
					};
				});
			}
			await Promise.all([refetch(), refetchVouchers()]);
		} finally {
			setSectionActionLoading(null);
		}
	};

	// Open section rejection dialog
	const handleOpenSectionRejectDialog = (sectionId: string) => {
		setSectionRejectId(sectionId);
		setSectionRejectReason('');
		setSectionRejectDialogOpen(true);
	};

	// Reject a single section (with revision tracking)
	const handleRejectSection = async () => {
		if (!voucher?.sectionResponses || !currentUser || !sectionRejectId) return;
		setSectionActionLoading(sectionRejectId);
		try {
			const now = new Date().toISOString();
			const updated = voucher.sectionResponses.map((sr) => {
				if (sr.sectionId !== sectionRejectId) return sr;
				// If re-rejecting a previously rejected section, archive the old rejection
				const history = [...(sr.revisionHistory || [])];
				if (sr.status === 'REJECTED' && sr.rejectedBy && sr.rejectedAt) {
					history.push({
						rejectedBy: sr.rejectedBy,
						rejectedAt: sr.rejectedAt,
						rejectionReason: sr.rejectionReason,
						resolvedBy: currentUser.id,
						resolvedAt: now,
						resolution: 'REJECTED' as const,
						fieldResponseSnapshot: sr.fieldResponses ? [...sr.fieldResponses] : null,
					});
				}
				return {
					...sr,
					status: 'REJECTED' as const,
					rejectedBy: currentUser.id,
					rejectedAt: now,
					rejectionReason: sectionRejectReason.trim() || null,
					// Clear verification fields on reject
					verifiedBy: null,
					verifiedAt: null,
					revisionHistory: history.length > 0 ? history : undefined,
				};
			});
			await updateVoucher({ sectionResponses: updated });
			// Mirror the rejection onto every sibling voucher in the same
			// payment unit so the group stays in sync.
			const reason = sectionRejectReason.trim() || null;
			if (siblingVouchers.length > 0) {
				await applySectionUpdateToSiblings(sectionRejectId, (sr) => {
					const history = [...(sr.revisionHistory || [])];
					if (sr.status === 'REJECTED' && sr.rejectedBy && sr.rejectedAt) {
						history.push({
							rejectedBy: sr.rejectedBy,
							rejectedAt: sr.rejectedAt,
							rejectionReason: sr.rejectionReason,
							resolvedBy: currentUser.id,
							resolvedAt: now,
							resolution: 'REJECTED' as const,
							fieldResponseSnapshot: sr.fieldResponses ? [...sr.fieldResponses] : null,
						});
					}
					return {
						...sr,
						status: 'REJECTED' as const,
						rejectedBy: currentUser.id,
						rejectedAt: now,
						rejectionReason: reason,
						verifiedBy: null,
						verifiedAt: null,
						revisionHistory: history.length > 0 ? history : undefined,
					};
				});
			}
			setSectionRejectDialogOpen(false);
			setSectionRejectId(null);
			setSectionRejectReason('');
			await Promise.all([refetch(), refetchVouchers()]);
		} catch (error) {
			console.error('Failed to reject section:', error);
			setBanner({ message: 'Failed to reject section. Please try again.', severity: 'error' });
		} finally {
			setSectionActionLoading(null);
		}
	};

	// ---------------------------------------------------------------------------
	// Inline field editing handlers (for PIU users fixing rejected sections)
	// ---------------------------------------------------------------------------

	/** Update a field value in local state (not yet saved) */
	const handleFieldEdit = (sectionId: string, fieldId: string, value: string) => {
		setEditingFieldValues((prev) => ({
			...prev,
			[`${sectionId}::${fieldId}`]: value,
		}));
	};

	/** Upload a document for a field in a rejected section */
	const handleFieldDocUpload = async (
		sectionId: string,
		fieldId: string,
		e: ChangeEvent<HTMLInputElement>
	) => {
		const files = e.target.files;
		if (!files || files.length === 0) return;
		if (!currentUser?.id || !project) return;

		const file = files[0];
		const key = `${sectionId}::${fieldId}`;
		setFieldUploading((prev) => ({ ...prev, [key]: true }));

		const contractId = contract?.id;
		try {
			const result = await uploadAiDocument({
				file,
				project_id: project.id,
				source_type: 'condition',
				source_id: condition?.id,
				tag: [
					condition?.id ? `condition:${condition.id}` : '',
					contractId ? `contract:${contractId}` : '',
					fieldId,
				]
					.filter(Boolean)
					.join(','),
			});
			setFieldUploadedDocs((prev) => ({
				...prev,
				[key]: { id: result.document_id, name: file.name },
			}));
			// Clear any "removed" flag since we now have a new doc
			setRemovedFieldDocs((prev) => {
				const next = { ...prev };
				delete next[key];
				return next;
			});
		} catch (err) {
			console.error(`Field doc upload failed (${sectionId}/${fieldId}):`, err);
		} finally {
			setFieldUploading((prev) => ({ ...prev, [key]: false }));
			e.target.value = '';
		}
	};

	/** Remove a document (newly-uploaded or existing saved doc) */
	const handleFieldDocRemove = (sectionId: string, fieldId: string) => {
		const key = `${sectionId}::${fieldId}`;
		// Remove newly-uploaded doc if present
		setFieldUploadedDocs((prev) => {
			const next = { ...prev };
			delete next[key];
			return next;
		});
		// Mark existing saved doc as removed
		setRemovedFieldDocs((prev) => ({ ...prev, [key]: true }));
	};

	/** Save all pending field edits for a section back to the voucher */
	const handleSaveSectionFields = async (sectionId: string) => {
		if (!voucher?.sectionResponses || !currentUser) return;
		setFieldSaveLoading(sectionId);
		try {
			const updated = voucher.sectionResponses.map((sr) => {
				if (sr.sectionId !== sectionId) return sr;
				const updatedFields = (sr.fieldResponses || []).map((fr) => {
					const valKey = `${sectionId}::${fr.fieldId}`;
					const docKey = `${sectionId}::${fr.fieldId}`;
					const editedValue = editingFieldValues[valKey];
					const uploadedDoc = fieldUploadedDocs[docKey];
					const isDocRemoved = !!removedFieldDocs[docKey];
					const hasChange = editedValue !== undefined || !!uploadedDoc || isDocRemoved;
					return {
						...fr,
						...(editedValue !== undefined ? { value: editedValue } : {}),
						...(uploadedDoc
							? { documentId: uploadedDoc.id, documentName: uploadedDoc.name }
							: isDocRemoved
								? { documentId: undefined, documentName: undefined }
								: {}),
						answeredAt: hasChange ? new Date().toISOString() : fr.answeredAt,
						answeredBy: hasChange ? currentUser.id : fr.answeredBy,
					};
				});
				return { ...sr, fieldResponses: updatedFields };
			});
			await updateVoucher({ sectionResponses: updated });
			// Mirror the resubmitted field values onto every sibling voucher's
			// matching section so the whole payment unit is back in PENDING
			// review with the same evidence.
			if (siblingVouchers.length > 0) {
				const updatedSection = updated.find((sr) => sr.sectionId === sectionId);
				if (updatedSection?.fieldResponses) {
					await applySectionUpdateToSiblings(sectionId, (sr) => ({
						...sr,
						fieldResponses: updatedSection.fieldResponses,
					}));
				}
			}
			// Clear local editing state for this section
			setEditingFieldValues((prev) => {
				const next = { ...prev };
				Object.keys(next).forEach((k) => {
					if (k.startsWith(`${sectionId}::`)) delete next[k];
				});
				return next;
			});
			setFieldUploadedDocs((prev) => {
				const next = { ...prev };
				Object.keys(next).forEach((k) => {
					if (k.startsWith(`${sectionId}::`)) delete next[k];
				});
				return next;
			});
			setRemovedFieldDocs((prev) => {
				const next = { ...prev };
				Object.keys(next).forEach((k) => {
					if (k.startsWith(`${sectionId}::`)) delete next[k];
				});
				return next;
			});
			// Mark this section as resubmitted so Reject/Approve buttons appear
			setResubmittedSections((prev) => ({ ...prev, [sectionId]: true }));
			await refetch();
		} catch (error) {
			console.error('Failed to save section fields:', error);
			setBanner({ message: 'Failed to save section fields. Please try again.', severity: 'error' });
		} finally {
			setFieldSaveLoading(null);
		}
	};

	/** Check if a section has any unsaved edits (including removals) */
	const sectionHasEdits = (sectionId: string): boolean => {
		return (
			Object.keys(editingFieldValues).some((k) => k.startsWith(`${sectionId}::`)) ||
			Object.keys(fieldUploadedDocs).some((k) => k.startsWith(`${sectionId}::`)) ||
			Object.keys(removedFieldDocs).some((k) => k.startsWith(`${sectionId}::`))
		);
	};

	/** Cancel all local edits for a section */
	const handleCancelSectionEdits = (sectionId: string) => {
		setEditingFieldValues((prev) => {
			const next = { ...prev };
			Object.keys(next).forEach((k) => {
				if (k.startsWith(`${sectionId}::`)) delete next[k];
			});
			return next;
		});
		setFieldUploadedDocs((prev) => {
			const next = { ...prev };
			Object.keys(next).forEach((k) => {
				if (k.startsWith(`${sectionId}::`)) delete next[k];
			});
			return next;
		});
		setRemovedFieldDocs((prev) => {
			const next = { ...prev };
			Object.keys(next).forEach((k) => {
				if (k.startsWith(`${sectionId}::`)) delete next[k];
			});
			return next;
		});
	};

	// Build merged sections list from condition verificationSections + voucher sectionResponses
	const mergedSections = useMemo(() => {
		const sections = condition?.verificationSections || [];
		const sectionResponses = voucher?.sectionResponses || [];

		// 1. Sections defined on the condition's verificationSections array.
		//    Since the API now composes template-sourced sections in here (each
		//    tagged with `source === 'TEMPLATE'` and a `sourceConditionName`),
		//    this single pass renders both milestone-owned and template-sourced
		//    requirements uniformly. Per-section sign-off works via the matching
		//    sectionResponse entry keyed by `section.id`.
		const conditionMerged = sections.map((section) => {
			const sectionResponse = sectionResponses.find((sr) => sr.sectionId === section.id);
			const isTemplate = section.source === 'TEMPLATE';
			return {
				section,
				sectionResponse: sectionResponse || null,
				isTemplate,
				conditionName: isTemplate
					? section.sourceConditionName || undefined
					: (undefined as string | undefined),
				fields: section.fields.map((field) => {
					const response = (sectionResponse?.fieldResponses || []).find(
						(r) => r.fieldId === field.id
					);
					return { field, response };
				}),
			};
		});

		// 2. Legacy fallback: template responses captured on older vouchers
		//    (created when templates were cloned as separate conditions and
		//    persisted via `isTemplate: true` SectionResponse entries). These
		//    are kept renderable so already-collected data is not lost; we
		//    dedupe against sections already surfaced via `conditionMerged`
		//    above so nothing appears twice as the system transitions.
		const surfacedSectionIds = new Set(conditionMerged.map((cm) => cm.section.id));
		const templateResponses = sectionResponses.filter(
			(sr) => sr.isTemplate && !surfacedSectionIds.has(sr.sectionId)
		);
		const templateMerged = templateResponses.map((sr) => {
			// Synthesize section & field definitions from response metadata
			const fields: DataRequestField[] = (sr.fieldResponses || []).map((fr) => ({
				id: fr.fieldId,
				label: fr.label,
				fieldType: fr.fieldType || 'text',
				required: false,
			}));
			const section: VerificationSection = {
				id: sr.sectionId,
				name: sr.sectionName,
				assignedTo: null,
				assignedGroupId: null,
				fields,
				status: sr.status,
				verifiedAt: sr.verifiedAt,
				verifiedBy: sr.verifiedBy,
				source: 'TEMPLATE',
				sourceConditionId: sr.conditionId ?? null,
				sourceConditionName: sr.conditionName ?? null,
			};
			return {
				section,
				sectionResponse: sr,
				isTemplate: true as const,
				conditionName: sr.conditionName,
				fields: fields.map((field) => {
					const response = (sr.fieldResponses || []).find((r) => r.fieldId === field.id);
					return { field, response };
				}),
			};
		});

		return [...conditionMerged, ...templateMerged];
	}, [condition?.verificationSections, voucher?.sectionResponses]);

	// All sections verified?
	const allSectionsVerified = useMemo(() => {
		if (mergedSections.length === 0) return true;
		return mergedSections.every((ms) => ms.sectionResponse?.status === 'VERIFIED');
	}, [mergedSections]);

	// Document viewer state
	const [viewerOpen, setViewerOpen] = useState(false);
	const [viewerDoc, setViewerDoc] = useState<{
		id: string;
		name: string;
		category: 'other';
		type: 'doc';
		size: string;
		uploadedAt: string;
		uploadedBy: string;
	} | null>(null);
	const [viewerBlobFilename, setViewerBlobFilename] = useState<string>('');
	const [viewerDocInfo, setViewerDocInfo] = useState<ViewerDocumentInfo | null>(null);
	// Not found state (only after loading is complete)
	if (!voucherLoading && !voucher) {
		return (
			<Box sx={{ px: 3, py: 4 }}>
				<Typography>{t('spendPlan.voucher.notFound', 'Voucher not found')}</Typography>
			</Box>
		);
	}

	// Computed values (safe for loading state)
	// Voucher and contract may be in different currencies (voucher follows the
	// source-account/earmark currency; contract is denominated in its own
	// currency, possibly several). Look up the contract amount in the voucher's
	// currency so the figure paired with the voucher's currency label is
	// actually denominated in that currency. When the contract carries no
	// amount in the voucher's currency, fall back to a per-currency breakdown
	// rather than coercing a number from a different currency.
	const contractAmountsByCurrency = contract ? getContractAmountsByCurrency(contract) : [];
	const voucherCurrency = voucher?.currency || contractAmountsByCurrency[0]?.currency || 'USD';
	const contractAmountInVoucherCurrency = contract
		? getContractAmountForCurrency(contract, voucherCurrency)
		: null;
	// Only fall back to the percentage-of-contract maths when the contract has
	// an amount denominated in the voucher's currency; mixing currencies in
	// that maths would compute the wrong payment value.
	const contractAmountForPercentageMaths = contractAmountInVoucherCurrency ?? 0;
	const paymentAmount = voucher
		? voucher.amount ||
			condition?.paymentAmount ||
			(condition?.paymentPercentage
				? (contractAmountForPercentageMaths * condition.paymentPercentage) / 100
				: 0)
		: 0;
	const paymentPercentage = condition?.paymentPercentage || 0;

	// Local restatement basis: the canonical resolver returns the contract's
	// implied FX rate (commitment scalars → contract amount rows → work plan
	// fallback). The rate lets us restate voucher-currency figures into the
	// project local currency, and acts as a ground-truth FX hint that defends
	// against older vouchers whose captured rate was stored inverted.
	const projectLocalCurrency = project?.currencies?.find((c) => c.type === 'LOCAL')?.currency;
	const reportingCurrency = getProjectReportingCurrency(project);
	const contractFx = resolveContractFxRate(contract, workPlan, project);
	const contractBudgetRate = contractFx?.rate ?? null;
	// Per-payment restatement. The voucher captures the FX rates entered at
	// payment time (each row: "1 row.currency = rate reporting"). They are the
	// authoritative basis for expressing a voucher-currency figure in the
	// project reporting and local currencies — e.g. an ETB payment on a USD
	// reporting project shows both ETB (local) and the USD equivalent. When no
	// captured rates exist, the local leg falls back to the contract's implied
	// budget rate so USD-contract vouchers still show a local equivalent.
	// The contract's budget rate is also passed as a ground-truth hint so
	// older vouchers (pre-PR-3862) that stored inverted rates don't render
	// pathological figures like $16M for a 100k ETB payment.
	const voucherRates = voucher?.exchangeRates ?? null;
	const localStatLine = (value: number): ReactNode => {
		const { reporting, local } = restateViaCapturedRates({
			amount: value,
			currency: voucherCurrency,
			reportingCurrency,
			localCurrency: projectLocalCurrency ?? null,
			rates: voucherRates,
			localPerReportingHint: contractBudgetRate ?? null,
		});
		// Final defensive local fallback via the contract budget rate when even
		// the hint-aware helper couldn't resolve a local leg (e.g. for a
		// reporting-currency voucher with no captured rates).
		let localValue = local;
		const repForLocal =
			reporting ?? (reportingCurrency && voucherCurrency === reportingCurrency ? value : null);
		if (
			localValue == null &&
			repForLocal != null &&
			contractBudgetRate != null &&
			projectLocalCurrency
		) {
			localValue = repForLocal * contractBudgetRate;
		}

		const lines: { currency: string; value: number }[] = [];
		if (reporting != null && reportingCurrency && voucherCurrency !== reportingCurrency) {
			lines.push({ currency: reportingCurrency, value: reporting });
		}
		if (
			localValue != null &&
			projectLocalCurrency &&
			voucherCurrency !== projectLocalCurrency &&
			projectLocalCurrency !== reportingCurrency
		) {
			lines.push({ currency: projectLocalCurrency, value: localValue });
		}
		if (lines.length === 0) return null;
		return lines.map((line) => (
			<Box
				key={line.currency}
				component="span"
				sx={{
					display: 'block',
					fontSize: { xs: '1.0625rem', sm: '1.15rem' },
					fontWeight: 500,
					color: '#6b7280',
				}}>
				{formatCurrency({ currency: line.currency, value: line.value })}
			</Box>
		));
	};
	const hasException = !!exception;
	const isReady = voucher?.status === 'READY' || voucher?.status === 'VERIFIED';
	const isRedeemed = voucher?.status === 'REDEEMED';
	const isCancelled = voucher?.status === 'CANCELLED';
	const showOverrideOption = !isReady && !hasException && !isRedeemed;

	// Check for exceptions (any yesno field answered "No") — from sectionResponses
	const sectionResponses = voucher?.sectionResponses || [];
	const hasChecklistException = sectionResponses.some((sr) => sr.hasException);

	// Show approval buttons for VERIFIED status (ready for review) only when all sections are verified
	const showApprovalButtons = voucher?.status === 'VERIFIED' && !isCancelled && allSectionsVerified;

	// Mark Paid is offered when a voucher has been approved and is waiting for
	// off-hub payment confirmation (the "Awaiting Payment" bucket on the Payments
	// page). The action mirrors the row button on the list page.
	const showMarkPaidAction = voucher?.status === 'READY' && !isCancelled;

	// Invoice recorded against this milestone (joined via milestoneNumber =
	// condition.id) — feeds the Mark Payment Complete prefill.
	const markPaidInvoice =
		condition && contract
			? getInvoicesByContract(contract.id).find((inv) => inv.milestoneNumber === condition.id)
			: undefined;

	// Drafts (GENERATED vouchers) belong to the Prepare-Payment workflow, not
	// this read-only voucher view. Surface a primary action that takes the user
	// back to the contract page with the dialog re-opened so they can finish
	// filling in amount / tax / earmark and submit.
	const isDraft = voucher?.status === 'GENERATED';
	const draftContractId =
		typeof voucher?.contract === 'number' ? voucher.contract : voucher?.contract?.id || null;
	const showContinueDraftAction = isDraft && !!draftContractId && !!project?.idWbg;
	const handleContinueDraft = () => {
		if (!draftContractId || !project?.idWbg) return;
		navigate(
			`/project/${project.idWbg}/spend-plans/contract/${draftContractId}?dialog=preparePayment&dialogContract=${draftContractId}`
		);
	};

	// Build stats for HeaderDetailPage (only when voucher is loaded)
	const headerStats = voucher
		? [
				{
					label: t('status'),
					value: (
						<VoucherStatusChip
							status={voucher.status}
							sx={{
								fontSize: { xs: '1rem', sm: '1.125rem' },
								height: 28,
								px: 1,
							}}
						/>
					),
				},
				{
					label: t('amount'),
					value: (
						<>
							{formatCurrency({ currency: voucherCurrency, value: paymentAmount })}
							{localStatLine(paymentAmount)}
						</>
					),
				},
				...(voucher.taxWithheld != null && voucher.taxWithheld > 0
					? [
							{
								label: t('spendPlan.taxWithheld'),
								value: formatCurrency({
									currency: voucherCurrency,
									value: getVoucherTaxWithheld(voucher),
								}),
							},
						]
					: []),
				...(paymentPercentage > 0
					? [
							{
								label: t('spendPlan.condition.paymentPercentage'),
								value: `${paymentPercentage}%`,
							},
						]
					: []),
				{
					label:
						voucher?.recipientType === 'BENEFICIARY'
							? t('beneficiary', 'Beneficiary')
							: t('supplier'),
					value: supplierName,
				},
				...(voucher.paymentRoute
					? [
							{
								label: t('invoice.paymentRoute'),
								value: (
									<Chip
										label={
											voucher.paymentRoute === 'DA_REIMBURSEMENT'
												? t('invoice.daReimbursement')
												: voucher.paymentRoute === 'DIRECT_PAYMENT'
													? t('invoice.directPayment')
													: voucher.paymentRoute
										}
										size="small"
										color="info"
										sx={{ fontWeight: 600, height: 28 }}
									/>
								),
							},
						]
					: []),
				// When the contract has an amount in the voucher's currency, render
				// that figure under a "Contract Value" stat in the voucher's
				// currency. Otherwise render one stat per contract currency so the
				// figure is always paired with the currency it is denominated in,
				// rather than a single number under the voucher's currency label.
				...(contractAmountInVoucherCurrency != null && contractAmountInVoucherCurrency > 0
					? [
							{
								label: t('spendPlan.condition.contractValue'),
								value: (
									<>
										{formatCurrency({
											currency: voucherCurrency,
											value: contractAmountInVoucherCurrency,
										})}
										{localStatLine(contractAmountInVoucherCurrency)}
									</>
								),
							},
						]
					: contractAmountsByCurrency
							.filter((a) => a.amount > 0)
							.map((a) => ({
								label: t('spendPlan.condition.contractValue'),
								value: formatCurrency({ currency: a.currency, value: a.amount }),
							}))),
			]
		: [];

	// Open document in the viewer dialog
	const handleOpenDocViewer = async (documentId: string, documentName: string) => {
		if (!documentName) return;
		setViewerDoc({
			id: documentId,
			name: documentName,
			category: 'other',
			type: 'doc',
			size: '',
			uploadedAt: '',
			uploadedBy: '',
		});
		setViewerBlobFilename(documentName);
		setViewerDocInfo({ name: documentName });
		setViewerOpen(true);

		// Fetch full document metadata (jobId, size, tags, source, uploadedAt, uploadedBy) from AI API
		try {
			const res = await fetchAiDocuments({
				search: documentName,
				project_id: project?.id,
				populate: 'uploaded_by,source',
			});
			const aiDoc = res.data?.find((d) => d.id === documentId) || res.data?.[0];
			if (aiDoc) {
				const metadata = aiDoc.metadata || {};
				const uploaderName =
					typeof aiDoc.uploaded_by === 'object' && aiDoc.uploaded_by
						? aiDoc.uploaded_by.displayName || aiDoc.uploaded_by.email
						: String(metadata.uploaded_by_name || '');
				const sizeBytes = Number(metadata.file_size_bytes || 0);
				const sizeStr =
					sizeBytes > 0
						? `${parseFloat((sizeBytes / Math.pow(1024, Math.floor(Math.log(sizeBytes) / Math.log(1024)))).toFixed(1))} ${['B', 'KB', 'MB', 'GB'][Math.floor(Math.log(sizeBytes) / Math.log(1024))]}`
						: undefined;
				const sourceType = (metadata.source_type as string) || undefined;
				const sourceLabel = aiDoc.source?.name || aiDoc.contract_name || undefined;
				setViewerDocInfo({
					id: aiDoc.id,
					name: aiDoc.filename,
					jobId: aiDoc.job_id || undefined,
					tags:
						Array.isArray(metadata.tags) && metadata.tags.length > 0
							? (metadata.tags as string[])
							: undefined,
					sourceType,
					sourceLabel,
					size: sizeStr,
					uploadedAt: aiDoc.uploaded_at,
					uploadedBy: uploaderName || '',
				});
			}
		} catch {
			// Metadata fetch failed — viewer still works, just without stats/insights
		}
	};

	// Field type display helpers
	const FIELD_TYPE_COLORS: Record<string, string> = {
		document: '#1976d2',
		yesno: '#7b1fa2',
		text: '#546e7a',
		number: '#00897b',
		date: '#e65100',
		select: '#5c6bc0',
	};

	const FIELD_TYPE_LABELS: Record<string, string> = {
		document: 'Document',
		yesno: 'Yes / No',
		text: 'Text',
		number: 'Number',
		date: 'Date',
		select: 'Dropdown',
	};

	return (
		<Box>
			{/* Header - Full width, always visible with loading state */}
			<HeaderDetailPage
				loading={voucherLoading}
				loadingActions={1}
				icon={hasException ? IconException : IconVoucher}
				breadcrumb={{
					label: t('payments'),
					to: `/project/${project?.idWbg}/spend-plans/vouchers`,
				}}
				title={condition?.name || (voucher ? `Voucher #${voucher.id}` : '')}
				variant="payment"
				stats={headerStats}
				extraChips={
					!voucherLoading && voucher ? (
						<>
							{hasException && (
								<Chip
									icon={<IconException sx={{ fontSize: '14px !important' }} />}
									label={t('spendPlan.exception')}
									size="small"
									sx={{
										bgcolor: alpha(COLORS.warning.main, 0.1),
										color: COLORS.warning.dark,
										fontWeight: 500,
										'& .MuiChip-icon': { color: COLORS.warning.dark },
									}}
								/>
							)}
							{hasChecklistException && !hasException && (
								<Chip
									icon={<IconException sx={{ fontSize: '14px !important' }} />}
									label={t('spendPlan.voucher.requirementException', 'Requirement Exception')}
									size="small"
									sx={{
										bgcolor: alpha(theme.palette.error.main, 0.1),
										color: theme.palette.error.dark,
										fontWeight: 500,
										'& .MuiChip-icon': { color: theme.palette.error.dark },
									}}
								/>
							)}
						</>
					) : undefined
				}
				customAction={
					!voucherLoading && voucher ? (
						<BtnSplit
							actions={[
								...(showContinueDraftAction
									? [
											{
												children: t('continueDraft'),
												onClick: handleContinueDraft,
												startIcon: <IconEditNote />,
											},
										]
									: []),
								...(showMarkPaidAction
									? [
											{
												children: t('spendPlan.voucher.markPaid', 'Manually Enter Payment Info'),
												onClick: () => setMarkPaidOpen(true),
												startIcon: <IconCheckCircle />,
											},
										]
									: []),
								...(showOverrideOption
									? [
											{
												children: t('spendPlan.override'),
												onClick: handleOverride,
												startIcon: <IconException />,
											},
										]
									: []),
								{
									children: t('viewAuditLog'),
									onClick: () => setAuditTimelineOpen(true),
									startIcon: <IconHistory />,
								},
								{
									children: t('viewDocuments'),
									onClick: () => setDocumentsDialogOpen(true),
									startIcon: <IconDocument />,
								},
							]}
						/>
					) : undefined
				}
			/>

			{/* Content with padding */}
			<Box sx={{ pb: 3, pt: 2, px: 3 }}>
				{/* Show skeleton for content while loading */}
				{voucherLoading ? (
					<PageDetailSkeleton leftCards={2} rightCards={2} />
				) : voucher ? (
					<>
						{/* Sibling vouchers — when a payment was split across earmarks
						    we surface the group as a condensed banner. The reviewer
						    can expand it to see every voucher in the unit. Verify /
						    approve actions on this page apply to all of them. */}
						{siblingVouchers.length > 0 && (
							<Paper
								elevation={0}
								sx={{
									bgcolor: alpha(theme.palette.primary.main, 0.04),
									border: '1px solid',
									borderColor: alpha(theme.palette.primary.main, 0.25),
									borderRadius: 1.5,
									mb: 2,
								}}>
								<Stack
									direction="row"
									alignItems="center"
									spacing={1.5}
									onClick={() => setSplitGroupExpanded((v) => !v)}
									sx={{
										cursor: 'pointer',
										px: 2,
										py: 1.25,
										'&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.06) },
									}}>
									<IconCallSplit sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
									<Box sx={{ flex: 1, minWidth: 0 }}>
										<Typography variant="body2" sx={{ fontWeight: 600, color: '#111827' }}>
											{t(
												'spendPlan.voucher.splitPaymentGroup',
												'Part of a {{count}}-voucher split payment',
												{ count: siblingVouchers.length + 1 }
											)}
										</Typography>
										<Typography variant="caption" color="text.secondary">
											{t(
												'spendPlan.voucher.splitPaymentGroupHelpShort',
												'Reviewed and approved as a single unit.'
											)}
										</Typography>
									</Box>
									<Stack direction="row" spacing={0.75} alignItems="center">
										{[voucher, ...siblingVouchers].slice(0, 4).map((v) => (
											<Chip
												key={v.id}
												size="small"
												label={`#${v.id}`}
												variant={v.id === voucher.id ? 'filled' : 'outlined'}
												color={v.id === voucher.id ? 'primary' : 'default'}
												sx={{ height: 28, fontSize: '0.875rem', fontWeight: 600, px: 0.5 }}
											/>
										))}
										{siblingVouchers.length + 1 > 4 && (
											<Typography variant="body2" color="text.secondary">
												+{siblingVouchers.length + 1 - 4}
											</Typography>
										)}
									</Stack>
									<IconButton size="small" sx={{ ml: 0.5 }}>
										<IconExpandMore
											sx={{
												transform: splitGroupExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
												transition: 'transform 0.2s',
											}}
										/>
									</IconButton>
								</Stack>
								<Collapse in={splitGroupExpanded} unmountOnExit>
									<Box sx={{ px: 2, pb: 2 }}>
										<VoucherPaymentReconciliation
											vouchers={[voucher, ...siblingVouchers]}
											reportingCurrency={
												project?.currencies?.find((c) => c.type === 'REPORTING')?.currency ||
												voucherCurrency
											}
											title={null}
											alwaysShow
											highlightVoucherId={voucher.id}
											onVoucherClick={(v) =>
												v.id !== voucher.id &&
												navigate(`/project/${project?.idWbg}/spend-plans/voucher/${v.id}`)
											}
										/>
									</Box>
								</Collapse>
							</Paper>
						)}

						{/* Supplier-generated draft notice — GENERATED vouchers on this
						    view arrived because the supplier filled in milestone
						    requirements from the EXT portal. The voucher itself is a
						    draft shell with no amount / tax / earmarking yet; finishing
						    it (or editing any of its fields) happens in Prepare a Payment
						    on the contract page, not here. */}
						{isDraft && (
							<Paper
								elevation={0}
								sx={{
									bgcolor: alpha(theme.palette.info.main, 0.06),
									border: '1px solid',
									borderColor: alpha(theme.palette.info.main, 0.4),
									borderRadius: 2,
									mb: 2,
									p: 2,
								}}>
								<Stack alignItems="flex-start" direction="row" spacing={2}>
									<IconInfo sx={{ color: theme.palette.info.main, mt: 0.25 }} />
									<Box sx={{ flex: 1 }}>
										<Typography
											sx={{ color: theme.palette.info.dark, fontWeight: 600 }}
											variant="subtitle2">
											Generated from supplier milestone submission
										</Typography>
										<Typography sx={{ color: theme.palette.info.dark, mt: 0.5 }} variant="body2">
											This draft voucher was created automatically when the supplier submitted
											requirement data for this milestone. To set the payment amount, tax,
											earmarking, or edit any of the captured fields, open{' '}
											<strong>Prepare a Payment</strong> from the contract page.
										</Typography>
										{showContinueDraftAction && (
											<Button
												size="small"
												variant="outlined"
												color="info"
												startIcon={<IconEditNote />}
												onClick={handleContinueDraft}
												sx={{ mt: 1.5, textTransform: 'none', fontWeight: 600 }}>
												Prepare a Payment
											</Button>
										)}
									</Box>
								</Stack>
							</Paper>
						)}

						{/* Exception Alert */}
						{hasException && exception && (
							<Paper
								elevation={0}
								sx={{
									bgcolor: alpha(COLORS.warning.main, 0.08),
									border: '1px solid',
									borderColor: COLORS.warning.main,
									borderRadius: 2,
									mb: 2,
									p: 2,
								}}>
								<Stack alignItems="flex-start" direction="row" spacing={2}>
									<IconException sx={{ color: COLORS.warning.main, mt: 0.25 }} />
									<Box sx={{ flex: 1 }}>
										<Typography
											sx={{ color: COLORS.warning.dark, fontWeight: 600 }}
											variant="subtitle2">
											{t('spendPlan.voucher.exceptionApplied', 'Condition Override Exception')}
										</Typography>
										<Typography sx={{ color: COLORS.warning.dark, mt: 0.5 }} variant="body2">
											{t('spendPlan.voucher.overriddenBy', {
												date: new Date(exception.created).toLocaleDateString(),
												name:
													typeof exception.overriddenBy === 'object'
														? exception.overriddenBy?.fullName
														: 'Unknown',
											})}
										</Typography>
										<Paper
											elevation={0}
											sx={{
												bgcolor: 'white',
												border: '1px solid',
												borderColor: alpha(COLORS.warning.main, 0.3),
												borderRadius: 1,
												mt: 1,
												p: 1.5,
											}}>
											<Typography color="text.secondary" sx={{ fontWeight: 600 }} variant="caption">
												{t('spendPlan.reason')}
											</Typography>
											<Typography variant="body2">{exception.reason}</Typography>
										</Paper>
									</Box>
								</Stack>
							</Paper>
						)}

						{/* Voucher comments (includes any skipped required-document reasons) */}
						{voucher?.comments && (
							<Paper
								elevation={0}
								sx={{
									bgcolor: alpha(theme.palette.info.main, 0.06),
									border: '1px solid',
									borderColor: alpha(theme.palette.info.main, 0.4),
									borderRadius: 2,
									mb: 2,
									p: 2,
								}}>
								<Stack alignItems="flex-start" direction="row" spacing={2}>
									<IconInfo sx={{ color: theme.palette.info.main, mt: 0.25 }} />
									<Box sx={{ flex: 1 }}>
										<Typography
											sx={{ color: theme.palette.info.dark, fontWeight: 600 }}
											variant="subtitle2">
											{t('spendPlan.voucher.comments', 'Comments')}
										</Typography>
										<Typography sx={{ mt: 0.5, whiteSpace: 'pre-line' }} variant="body2">
											{voucher.comments}
										</Typography>
									</Box>
								</Stack>
							</Paper>
						)}

						{/* Main Layout: Left (8/12) + Right (4/12) columns */}
						<Grid container spacing={2}>
							{/* Left Column: Condition Details + Documents */}
							<Grid item lg={8} xs={12}>
								<Stack spacing={2}>
									{/* Details Card */}
									<CardDetail
										icon={IconInfo}
										title={t('spendPlan.details', 'Details')}
										variant="payment"
										themeColor={GRADIENTS.payment.icon}>
										<Box sx={{ p: 2 }}>
											<Stack spacing={2}>
												<Box>
													<Typography
														color="text.secondary"
														sx={{ fontWeight: 600 }}
														variant="caption">
														{t('spendPlan.condition.name', 'Condition Name')}
													</Typography>
													<Typography sx={{ fontWeight: 500 }} variant="body1">
														{condition?.name || `Voucher #${voucher.id}`}
													</Typography>
												</Box>
												{condition?.description && (
													<Box>
														<Typography
															color="text.secondary"
															sx={{ fontWeight: 600 }}
															variant="caption">
															{t('spendPlan.condition.description', 'Condition Description')}
														</Typography>
														<Typography color="text.secondary" variant="body2">
															{condition.description}
														</Typography>
													</Box>
												)}
											</Stack>
											<PaymentTimeline
												voucher={voucher}
												embedded
												creator={
													lifecycle['VOUCHER_CREATED']?.actor ??
													(voucher.createdBy
														? typeof voucher.createdBy === 'object'
															? voucher.createdBy
															: projectUsers.find((u) => u.id === voucher.createdBy) ||
																(currentUser?.id === voucher.createdBy ? currentUser : null)
														: null)
												}
												approver={
													lifecycle['VOUCHER_APPROVED']?.actor ??
													(voucher.approvedBy
														? typeof voucher.approvedBy === 'object'
															? voucher.approvedBy
															: projectUsers.find((u) => u.id === voucher.approvedBy) ||
																(currentUser?.id === voucher.approvedBy ? currentUser : null)
														: null)
												}
												rejecter={
													lifecycle['VOUCHER_REJECTED']?.actor ??
													(voucher.rejectedBy
														? typeof voucher.rejectedBy === 'object'
															? voucher.rejectedBy
															: projectUsers.find((u) => u.id === voucher.rejectedBy) ||
																(currentUser?.id === voucher.rejectedBy ? currentUser : null)
														: null)
												}
												redeemer={
													lifecycle['VOUCHER_REDEEMED']?.actor ??
													(voucher.redeemedBy
														? typeof voucher.redeemedBy === 'object'
															? voucher.redeemedBy
															: projectUsers.find((u) => u.id === voucher.redeemedBy) ||
																(currentUser?.id === voucher.redeemedBy ? currentUser : null)
														: null)
												}
												approvedAt={lifecycle['VOUCHER_APPROVED']?.created}
												rejectedAt={lifecycle['VOUCHER_REJECTED']?.created}
												redeemedAt={lifecycle['VOUCHER_REDEEMED']?.created}
											/>
										</Box>
									</CardDetail>

									{/* Financing Sources Card — per-loan breakdown for vouchers
									    that split the payment across more than one project loan.
									    Single-source vouchers are uninteresting (the loan is
									    already implicit from the voucher.loan field), so we hide
									    the card to avoid visual noise. */}
									{voucher.financingSources && voucher.financingSources.length > 1 && (
										<CardDetail
											icon={IconCallSplit}
											title={t('voucher.financingSources', 'Financing Sources')}
											variant="payment"
											themeColor={GRADIENTS.payment.icon}>
											<Box sx={{ p: 2 }}>
												{(() => {
													const sources = voucher.financingSources!;
													const totalGross = sources.reduce(
														(t, s) => t + Number(s.amount || 0) + Number(s.tax || 0),
														0
													);
													return (
														<Stack spacing={1}>
															<Stack
																direction="row"
																spacing={2}
																sx={{
																	px: 1,
																	pb: 0.5,
																	borderBottom: 1,
																	borderColor: 'divider',
																}}>
																<Typography
																	variant="caption"
																	color="text.secondary"
																	sx={{ fontWeight: 600, flex: 2 }}>
																	{t('voucher.loan', 'Loan')}
																</Typography>
																<Typography
																	variant="caption"
																	color="text.secondary"
																	sx={{ fontWeight: 600, flex: 1, textAlign: 'right' }}>
																	{t('amount', 'Amount')}
																</Typography>
																<Typography
																	variant="caption"
																	color="text.secondary"
																	sx={{ fontWeight: 600, flex: 1, textAlign: 'right' }}>
																	{t('voucher.tax', 'Tax')}
																</Typography>
																<Typography
																	variant="caption"
																	color="text.secondary"
																	sx={{ fontWeight: 600, flex: 1, textAlign: 'right' }}>
																	%
																</Typography>
															</Stack>
															{sources.map((s, i) => {
																const gross = Number(s.amount || 0) + Number(s.tax || 0);
																const pct = totalGross > 0 ? (gross / totalGross) * 100 : 0;
																const loan = projectLoans.find((l) => l.id === s.loanId);
																const loanLabel =
																	loan?.code ||
																	(loan?.financier && loan?.num
																		? `${loan.financier}${loan.num}`
																		: '') ||
																	loan?.name ||
																	`Loan #${s.loanId}`;
																return (
																	<Stack
																		key={`${s.loanId}-${i}`}
																		direction="row"
																		spacing={2}
																		sx={{ px: 1 }}>
																		<Typography variant="body2" sx={{ flex: 2 }}>
																			{loanLabel}
																		</Typography>
																		<Typography
																			variant="body2"
																			sx={{ flex: 1, textAlign: 'right' }}>
																			{formatCurrency({
																				currency: voucherCurrency,
																				value: Number(s.amount || 0),
																			})}
																		</Typography>
																		<Typography
																			variant="body2"
																			sx={{ flex: 1, textAlign: 'right' }}>
																			{formatCurrency({
																				currency: voucherCurrency,
																				value: Number(s.tax || 0),
																			})}
																		</Typography>
																		<Typography
																			variant="body2"
																			sx={{ flex: 1, textAlign: 'right' }}>
																			{`${pct.toFixed(1)}%`}
																		</Typography>
																	</Stack>
																);
															})}
															<Stack
																direction="row"
																spacing={2}
																sx={{
																	px: 1,
																	pt: 0.5,
																	borderTop: 1,
																	borderColor: 'divider',
																}}>
																<Typography variant="body2" sx={{ fontWeight: 600, flex: 2 }}>
																	{t('total', 'Total')}
																</Typography>
																<Typography
																	variant="body2"
																	sx={{ fontWeight: 600, flex: 1, textAlign: 'right' }}>
																	{formatCurrency({
																		currency: voucherCurrency,
																		value: sources.reduce((t, s) => t + Number(s.amount || 0), 0),
																	})}
																</Typography>
																<Typography
																	variant="body2"
																	sx={{ fontWeight: 600, flex: 1, textAlign: 'right' }}>
																	{formatCurrency({
																		currency: voucherCurrency,
																		value: sources.reduce((t, s) => t + Number(s.tax || 0), 0),
																	})}
																</Typography>
																<Typography
																	variant="body2"
																	sx={{ fontWeight: 600, flex: 1, textAlign: 'right' }}>
																	100.0%
																</Typography>
															</Stack>
														</Stack>
													);
												})()}
											</Box>
										</CardDetail>
									)}

									{/* Invoice card(s) linked to this milestone */}
									{condition &&
										contract &&
										getInvoicesByContract(contract.id)
											.filter((inv) => inv.milestoneNumber === condition.id)
											.map((inv) => (
												<Box key={inv.id} sx={{ mb: 2 }}>
													<CardInvoiceDetails invoice={inv} />
												</Box>
											))}

									{/* Requirements Card — grouped view by section */}
									{mergedSections.length > 0 && (
										<CardDetail
											icon={IconGroup}
											title={t('spendPlan.requirements')}
											variant="condition"
											themeColor={GRADIENTS.condition.icon}
											headerActions={(() => {
												const total = mergedSections.length;
												const verified = mergedSections.filter(
													(ms) => ms.sectionResponse?.status === 'VERIFIED'
												).length;
												const rejected = mergedSections.filter(
													(ms) => ms.sectionResponse?.status === 'REJECTED'
												).length;
												const pct = total > 0 ? (verified / total) * 100 : 0;
												const barColor =
													rejected > 0
														? theme.palette.error.main
														: verified === total
															? COLORS.success.main
															: theme.palette.primary.main;
												const statusLabel =
													rejected > 0
														? `${rejected} rejected`
														: verified === total
															? t('allVerified')
															: `${verified} of ${total}`;
												return (
													<Stack
														direction="row"
														spacing={1.5}
														alignItems="center"
														sx={{ minWidth: 160 }}>
														<LinearProgress
															variant="determinate"
															value={pct}
															sx={{
																flex: 1,
																height: 6,
																borderRadius: 3,
																bgcolor: alpha(barColor, 0.15),
																'& .MuiLinearProgress-bar': {
																	borderRadius: 3,
																	bgcolor: barColor,
																},
															}}
														/>
														<Typography
															sx={{
																fontSize: '1.25rem',
																fontWeight: 600,
																color: barColor,
																whiteSpace: 'nowrap',
															}}>
															{statusLabel}
														</Typography>
													</Stack>
												);
											})()}>
											<Box sx={{ py: 1 }}>
												{mergedSections.map(
													(
														{
															section,
															sectionResponse,
															isTemplate: isTpl,
															conditionName: tplCondName,
															fields: sectionFields,
														},
														sIdx
													) => {
														const assignee =
															projectUsers.find((u) => u.id === section.assignedTo) ||
															(currentUser?.id === section.assignedTo ? currentUser : null);
														const verifier = sectionResponse?.verifiedBy
															? projectUsers.find((u) => u.id === sectionResponse.verifiedBy) ||
																(currentUser?.id === sectionResponse.verifiedBy
																	? currentUser
																	: null)
															: null;
														const rejecter = sectionResponse?.rejectedBy
															? projectUsers.find((u) => u.id === sectionResponse.rejectedBy) ||
																(currentUser?.id === sectionResponse.rejectedBy
																	? currentUser
																	: null)
															: null;
														// Supplier who submitted this section. PIU submitters resolve
														// from the project users by id; EXT supplier users are not in
														// that list, so fall back to the supplier entity name rather
														// than showing "Unknown".
														const submitter =
															sectionResponse?.submittedBy != null
																? projectUsers.find((u) => u.id === sectionResponse.submittedBy) ||
																	(currentUser?.id === sectionResponse.submittedBy
																		? currentUser
																		: null) ||
																	(supplierName && supplierName !== 'Unknown Supplier'
																		? ({ firstName: supplierName, lastName: '' } as Partial<User>)
																		: null)
																: null;
														const sectionStatus =
															sectionResponse?.status || section.status || 'PENDING';
														const isPending = sectionStatus === 'PENDING';
														const isRejected = sectionStatus === 'REJECTED';
														const isVoucherVerified = voucher?.status === 'VERIFIED';
														// Reviewers can act on PENDING or REJECTED sections
														const canAct =
															(isPending || isRejected) && isVoucherVerified && !isCancelled;
														// PIU users can edit fields on rejected sections
														const canEditFields = isRejected && isVoucherVerified && !isCancelled;
														// Derive "resubmitted" from persisted data so it survives page refresh:
														// if any field was answered after the section was rejected, it's been resubmitted
														const resubmittedFieldResponses =
															isRejected && sectionResponse?.rejectedAt
																? sectionFields
																		.filter(
																			({ response }) =>
																				!!response?.answeredAt &&
																				new Date(response.answeredAt).getTime() >
																					new Date(sectionResponse.rejectedAt as string).getTime()
																		)
																		.map(({ response }) => response)
																		.filter((r): r is NonNullable<typeof r> => !!r)
																: [];
														const sectionWasResubmitted = resubmittedFieldResponses.length > 0;
														const resubmittedAt = sectionWasResubmitted
															? resubmittedFieldResponses.reduce((latest, r) =>
																	new Date(r.answeredAt as string).getTime() >
																	new Date(latest.answeredAt as string).getTime()
																		? r
																		: latest
																).answeredAt
															: null;
														const resubmittedBy = sectionWasResubmitted
															? resubmittedFieldResponses.reduce((latest, r) =>
																	new Date(r.answeredAt as string).getTime() >
																	new Date(latest.answeredAt as string).getTime()
																		? r
																		: latest
																).answeredBy
															: null;

														return (
															<Box key={section.id}>
																{sIdx > 0 && <Divider sx={{ mx: 2, my: 0.5, opacity: 0.4 }} />}
																{/* Section header */}
																{(() => {
																	const answeredFieldCount = sectionFields.filter(
																		({ response }) =>
																			!!response &&
																			(response.value !== null || !!response.documentId)
																	).length;
																	const totalFieldCount = sectionFields.length;
																	return (
																		<Box
																			sx={{
																				display: 'flex',
																				alignItems: 'center',
																				gap: 0.75,
																				px: 2,
																				pt: 1,
																				pb: 0.25,
																			}}>
																			<IconGroup
																				sx={{
																					fontSize: 16,
																					color: 'primary.main',
																				}}
																			/>
																			<Typography
																				sx={{
																					fontSize: '1.1rem',
																					fontWeight: 700,
																					color: theme.palette.grey[500],
																					textTransform: 'uppercase',
																					letterSpacing: '0.1em',
																				}}>
																				{section.name || `Section ${sIdx + 1}`}
																			</Typography>
																			{isTpl && tplCondName && (
																				<Chip
																					label={tplCondName}
																					size="small"
																					sx={{
																						fontSize: '1.2rem',
																						bgcolor: alpha('#8b5cf6', 0.12),
																						color: '#8b5cf6',
																						fontWeight: 600,
																					}}
																				/>
																			)}
																			{assignee && (
																				<Chip
																					label={`${assignee.firstName} ${assignee.lastName}`}
																					size="small"
																					variant="outlined"
																					sx={{
																						height: 20,
																						fontSize: '0.7rem',
																					}}
																				/>
																			)}
																			<Typography
																				sx={{
																					fontSize: '1.1rem',
																					fontWeight: 700,
																					color:
																						answeredFieldCount === totalFieldCount
																							? COLORS.success.main
																							: theme.palette.grey[500],
																				}}>
																				{answeredFieldCount}/{totalFieldCount}
																			</Typography>
																			{/* Right-aligned audit info */}
																			<Box sx={{ ml: 'auto' }}>
																				{sectionStatus === 'VERIFIED' && (
																					<UserStamp
																						user={verifier}
																						label="Verified"
																						color={COLORS.success.main}
																						timestamp={sectionResponse?.verifiedAt}
																						fontSize="1.25rem"
																						size={30}
																					/>
																				)}
																				{sectionStatus === 'REJECTED' && (
																					<UserStamp
																						user={rejecter}
																						label="Rejected"
																						color={theme.palette.error.main}
																						timestamp={sectionResponse?.rejectedAt}
																						fontSize="1.25rem"
																						size={30}
																					/>
																				)}
																				{sectionStatus !== 'VERIFIED' &&
																					sectionStatus !== 'REJECTED' &&
																					sectionResponse?.submittedAt && (
																						<UserStamp
																							user={submitter}
																							label="Submitted"
																							color={theme.palette.info.main}
																							timestamp={sectionResponse.submittedAt}
																							fontSize="1.25rem"
																							size={30}
																						/>
																					)}
																			</Box>
																		</Box>
																	);
																})()}

																{/* ── Verification Lifecycle Timeline ── */}
																{(() => {
																	// Build chronological timeline entries
																	type FieldRow = {
																		label: string;
																		value: string | null;
																		fieldType: string;
																		note?: string;
																		color: string;
																	};
																	type TimelineEntry = {
																		type:
																			| 'exception'
																			| 'rejected'
																			| 'resubmitted'
																			| 'verified'
																			| 'revision-rejected'
																			| 'revision-resolved';
																		timestamp: string | null;
																		userId: number | null;
																		detail?: string;
																		fieldRows?: FieldRow[];
																	};
																	const entries: TimelineEntry[] = [];

																	// Helper: get field type color
																	const ftColor = (ft: string) =>
																		FIELD_TYPE_COLORS[ft] || '#546e7a';

																	// 1. Exception — fields where the answer didn't match
																	if (sectionResponse?.hasException) {
																		const firstAnsweredAt =
																			sectionFields
																				.filter(
																					({ response }) =>
																						!!response?.exceptionNote && response?.answeredAt
																				)
																				.map(({ response }) => response?.answeredAt as string)
																				.sort()[0] || null;
																		const firstAnsweredBy = firstAnsweredAt
																			? (sectionFields.find(
																					({ response }) => response?.answeredAt === firstAnsweredAt
																				)?.response?.answeredBy ?? null)
																			: null;
																		const exceptionRows: FieldRow[] = sectionFields
																			.filter(({ response }) => !!response?.exceptionNote)
																			.map(({ field, response }) => ({
																				label: field.label,
																				value: response?.value ?? null,
																				fieldType: field.fieldType,
																				note: response?.exceptionNote || undefined,
																				color: ftColor(field.fieldType),
																			}));
																		entries.push({
																			type: 'exception',
																			timestamp: firstAnsweredAt,
																			userId: firstAnsweredBy,
																			fieldRows:
																				exceptionRows.length > 0 ? exceptionRows : undefined,
																		});
																	}

																	// 2. Revision history (past rejection→resolution cycles)
																	if (sectionResponse?.revisionHistory) {
																		for (const rev of sectionResponse.revisionHistory) {
																			// Build field rows from snapshot (captured at resolution time = resubmitted values)
																			const snapRows: FieldRow[] = (
																				rev.fieldResponseSnapshot || []
																			).map((snap) => {
																				const fieldDef = sectionFields.find(
																					({ field }) => field.id === snap.fieldId
																				);
																				const ft =
																					fieldDef?.field.fieldType || snap.fieldType || 'text';
																				return {
																					label:
																						fieldDef?.field.label || snap.label || snap.fieldId,
																					value: snap.value,
																					fieldType: ft,
																					note: snap.exceptionNote || undefined,
																					color: ftColor(ft),
																				};
																			});
																			entries.push({
																				type: 'revision-rejected',
																				timestamp: rev.rejectedAt,
																				userId: rev.rejectedBy,
																				detail: rev.rejectionReason || undefined,
																			});
																			// Derive resubmission from snapshot field timestamps (latest answeredAt)
																			if (
																				rev.fieldResponseSnapshot &&
																				rev.fieldResponseSnapshot.length > 0
																			) {
																				const answeredAts = rev.fieldResponseSnapshot
																					.filter((s) => s.answeredAt)
																					.map((s) => ({
																						at: s.answeredAt as string,
																						by: s.answeredBy ?? null,
																					}));
																				const latest = answeredAts.sort(
																					(a, b) =>
																						new Date(b.at).getTime() - new Date(a.at).getTime()
																				)[0];
																				if (latest) {
																					entries.push({
																						type: 'resubmitted',
																						timestamp: latest.at,
																						userId: latest.by,
																						fieldRows: snapRows.length > 0 ? snapRows : undefined,
																					});
																				}
																			}
																			entries.push({
																				type: 'revision-resolved',
																				timestamp: rev.resolvedAt,
																				userId: rev.resolvedBy,
																				detail:
																					rev.resolution === 'VERIFIED'
																						? 'Approved'
																						: 'Rejected again',
																			});
																		}
																	}

																	// 3. Current rejection
																	if (sectionResponse?.rejectedBy && sectionResponse?.rejectedAt) {
																		entries.push({
																			type: 'rejected',
																			timestamp: sectionResponse.rejectedAt,
																			userId: sectionResponse.rejectedBy,
																			detail: sectionResponse.rejectionReason || undefined,
																		});
																	}

																	// 4. Resubmission
																	if (sectionWasResubmitted || resubmittedSections[section.id]) {
																		const resubRows: FieldRow[] = resubmittedFieldResponses
																			.filter((r) => r.value)
																			.map((r) => {
																				const fieldDef = sectionFields.find(
																					({ field }) => field.id === r.fieldId
																				);
																				const ft =
																					fieldDef?.field.fieldType || r.fieldType || 'text';
																				return {
																					label: fieldDef?.field.label || r.label || r.fieldId,
																					value: r.value,
																					fieldType: ft,
																					color: ftColor(ft),
																				};
																			});
																		entries.push({
																			type: 'resubmitted',
																			timestamp: resubmittedAt ?? null,
																			userId: resubmittedBy ?? null,
																			fieldRows: resubRows.length > 0 ? resubRows : undefined,
																		});
																	}

																	// 5. Final verification (skip if already covered by a revision-resolved "Approved")
																	const alreadyResolvedAsVerified = entries.some(
																		(e) => e.type === 'revision-resolved' && e.detail === 'Approved'
																	);
																	if (
																		sectionStatus === 'VERIFIED' &&
																		sectionResponse?.verifiedBy &&
																		!alreadyResolvedAsVerified
																	) {
																		entries.push({
																			type: 'verified',
																			timestamp: sectionResponse.verifiedAt ?? null,
																			userId: sectionResponse.verifiedBy,
																		});
																	}

																	if (entries.length === 0) return null;

																	// Logical phase ordering — exception always before rejection,
																	// resubmission after rejection, resolution/verification last.
																	// Timestamp is tiebreaker within the same phase only.
																	const PHASE: Record<string, number> = {
																		exception: 0,
																		'revision-rejected': 1,
																		rejected: 1,
																		resubmitted: 2,
																		'revision-resolved': 3,
																		verified: 4,
																	};
																	entries.sort((a, b) => {
																		const pa = PHASE[a.type] ?? 99;
																		const pb = PHASE[b.type] ?? 99;
																		if (pa !== pb) return pa - pb;
																		if (!a.timestamp) return -1;
																		if (!b.timestamp) return 1;
																		return (
																			new Date(a.timestamp).getTime() -
																			new Date(b.timestamp).getTime()
																		);
																	});

																	const lookupUser = (userId: number | null) => {
																		if (!userId) return null;
																		return (
																			projectUsers.find((u) => u.id === userId) ||
																			(currentUser?.id === userId ? currentUser : null)
																		);
																	};

																	const formatTs = (ts: string | null) => {
																		if (!ts) return '';
																		const d = new Date(ts);
																		return `${d.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' })} ${d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}`;
																	};

																	const entryMeta: Record<
																		string,
																		{ color: string; icon: React.ReactNode; label: string }
																	> = {
																		exception: {
																			color: theme.palette.warning.main,
																			icon: <IconException sx={{ fontSize: 18 }} />,
																			label: 'Requirement Exception',
																		},
																		rejected: {
																			color: theme.palette.error.main,
																			icon: <IconReject sx={{ fontSize: 18 }} />,
																			label: 'Rejected',
																		},
																		'revision-rejected': {
																			color: theme.palette.error.main,
																			icon: <IconReject sx={{ fontSize: 18 }} />,
																			label: 'Rejected',
																		},
																		resubmitted: {
																			color: COLORS.info.main,
																			icon: <IconChecklist sx={{ fontSize: 18 }} />,
																			label: 'Resubmitted',
																		},
																		'revision-resolved': {
																			color: COLORS.success.main,
																			icon: <IconApprove sx={{ fontSize: 18 }} />,
																			label: 'Resolved',
																		},
																		verified: {
																			color: COLORS.success.main,
																			icon: <IconVerified sx={{ fontSize: 18 }} />,
																			label: 'Verified',
																		},
																	};

																	return (
																		<Box sx={{ mx: 2, mt: 0.5, mb: 0.5 }}>
																			{entries.map((entry, eIdx) => {
																				const meta = entryMeta[entry.type];
																				const user = lookupUser(entry.userId);
																				const userName = user
																					? `${user.firstName} ${user.lastName}`.trim()
																					: null;
																				const isLast = eIdx === entries.length - 1;

																				return (
																					<Box key={eIdx} sx={{ display: 'flex', gap: 1.5 }}>
																						{/* Timeline rail */}
																						<Box
																							sx={{
																								display: 'flex',
																								flexDirection: 'column',
																								alignItems: 'center',
																								pt: 0.25,
																							}}>
																							<Box
																								sx={{
																									width: 28,
																									height: 28,
																									borderRadius: '50%',
																									bgcolor: alpha(meta.color, 0.12),
																									display: 'flex',
																									alignItems: 'center',
																									justifyContent: 'center',
																									color: meta.color,
																									flexShrink: 0,
																								}}>
																								{meta.icon}
																							</Box>
																							{!isLast && (
																								<Box
																									sx={{
																										width: 2,
																										flex: 1,
																										bgcolor: alpha(meta.color, 0.2),
																										mt: 0.5,
																										mb: 0.5,
																										minHeight: 16,
																									}}
																								/>
																							)}
																						</Box>

																						{/* Content */}
																						<Box sx={{ flex: 1, pb: isLast ? 0 : 1, minWidth: 0 }}>
																							<Stack
																								direction="row"
																								alignItems="center"
																								spacing={0.75}
																								sx={{ flexWrap: 'wrap' }}>
																								<Typography
																									sx={{
																										fontSize: '1.15rem',
																										fontWeight: 700,
																										color: meta.color,
																										textTransform: 'uppercase',
																										letterSpacing: '0.04em',
																									}}>
																									{meta.label}
																								</Typography>
																								{userName && (
																									<Typography
																										sx={{
																											fontSize: '1.1rem',
																											color: 'text.secondary',
																										}}>
																										by {userName}
																									</Typography>
																								)}
																								{entry.timestamp && (
																									<Typography
																										sx={{
																											fontSize: '1.1rem',
																											color: 'text.disabled',
																											ml: 'auto',
																											flexShrink: 0,
																										}}>
																										{formatTs(entry.timestamp)}
																									</Typography>
																								)}
																							</Stack>

																							{/* Detail text (rejection reason, etc.) */}
																							{entry.detail && (
																								<Typography
																									sx={{
																										fontSize: '1.15rem',
																										color: 'text.secondary',
																										mt: 0.25,
																										lineHeight: 1.4,
																									}}>
																									{entry.detail}
																								</Typography>
																							)}

																							{/* Field rows — consistent with the read-only field display style */}
																							{entry.fieldRows && entry.fieldRows.length > 0 && (
																								<Box sx={{ mt: 0.5 }}>
																									{entry.fieldRows.map((fr, fIdx) => (
																										<Stack
																											key={fIdx}
																											direction="row"
																											alignItems="center"
																											spacing={1}
																											sx={{ py: 0.25, pl: 1 }}>
																											<Box
																												sx={{
																													width: 8,
																													height: 8,
																													borderRadius: '50%',
																													bgcolor: fr.value
																														? fr.color
																														: alpha(fr.color, 0.4),
																													flexShrink: 0,
																												}}
																											/>
																											<Typography
																												sx={{
																													fontSize: '1.1rem',
																													fontWeight: 500,
																													color: '#1F2937',
																												}}
																												noWrap>
																												{fr.label}
																											</Typography>
																											<Typography
																												sx={{
																													color: '#D1D5DB',
																													fontSize: '1.1rem',
																													flexShrink: 0,
																												}}>
																												—
																											</Typography>
																											<Typography
																												sx={{
																													fontSize: '1.1rem',
																													fontWeight: 500,
																													color: fr.value ? '#374151' : '#D1D5DB',
																													fontStyle: fr.value ? 'normal' : 'italic',
																												}}
																												noWrap>
																												{fr.value || t('noAnswer')}
																											</Typography>
																											<Typography
																												sx={{
																													fontSize: '1.05rem',
																													color: fr.color,
																													fontWeight: 600,
																													opacity: 0.7,
																													flexShrink: 0,
																												}}>
																												{FIELD_TYPE_LABELS[fr.fieldType] ||
																													fr.fieldType}
																											</Typography>
																											{fr.note && (
																												<Typography
																													sx={{
																														fontSize: '1.05rem',
																														color: 'text.secondary',
																														fontStyle: 'italic',
																														ml: 0.5,
																													}}
																													noWrap>
																													— {fr.note}
																												</Typography>
																											)}
																										</Stack>
																									))}
																								</Box>
																							)}
																						</Box>
																					</Box>
																				);
																			})}
																		</Box>
																	);
																})()}

																{/* Section fields */}
																<Box sx={{ pb: 1 }}>
																	{sectionFields.length === 0 ? (
																		<Typography
																			variant="body2"
																			color="text.secondary"
																			sx={{
																				fontStyle: 'italic',
																				px: 2,
																				py: 1,
																			}}>
																			No requirements in this section
																		</Typography>
																	) : (
																		sectionFields.map(({ field, response }) => {
																			const accentColor =
																				FIELD_TYPE_COLORS[field.fieldType] || '#546e7a';
																			const isRequired = !!field.required;
																			const isOverridden = !!response?.overridden;
																			const editKey = `${section.id}::${field.id}`;
																			const editedValue = editingFieldValues[editKey];
																			const uploadedDoc = fieldUploadedDocs[editKey];
																			const isUploadingThis = !!fieldUploading[editKey];
																			const isDocRemoved = !!removedFieldDocs[editKey];
																			// Effective values (prefer local edits over saved)
																			const effectiveValue = editedValue ?? response?.value ?? null;
																			const effectiveDocId =
																				uploadedDoc?.id ??
																				(isDocRemoved ? null : (response?.documentId ?? null));
																			const effectiveDocName =
																				uploadedDoc?.name ??
																				(isDocRemoved ? null : (response?.documentName ?? null));
																			const hasResponse =
																				effectiveValue !== null || !!effectiveDocId;

																			return (
																				<Box key={field.id}>
																					{canEditFields ? (
																						/* ===== EDITABLE MODE ===== */
																						<Box sx={{ py: 0.75, px: 2, pl: 4 }}>
																							<Stack
																								direction="row"
																								alignItems="center"
																								spacing={1}
																								sx={{ mb: 0.5 }}>
																								<Box
																									sx={{
																										width: 8,
																										height: 8,
																										borderRadius: '50%',
																										bgcolor: hasResponse
																											? COLORS.success.main
																											: alpha(accentColor, 0.4),
																										flexShrink: 0,
																									}}
																								/>
																								<Typography
																									sx={{
																										fontSize: '1.25rem',
																										fontWeight: 600,
																										color: '#1F2937',
																									}}>
																									{field.label}
																									{isRequired && (
																										<Typography
																											component="span"
																											color="error"
																											sx={{ ml: 0.5 }}>
																											*
																										</Typography>
																									)}
																								</Typography>
																								<Typography
																									sx={{
																										fontSize: '1.25rem',
																										color: accentColor,
																										fontWeight: 600,
																										opacity: 0.7,
																									}}>
																									{FIELD_TYPE_LABELS[field.fieldType] ||
																										field.fieldType}
																								</Typography>
																							</Stack>

																							{/* Editable input by field type */}
																							{(field.fieldType === 'text' ||
																								field.fieldType === 'number' ||
																								field.fieldType === 'date') && (
																								<TextField
																									fullWidth={field.fieldType === 'text'}
																									placeholder={
																										field.placeholder ||
																										`Enter ${field.fieldType}...`
																									}
																									size="small"
																									sx={{
																										width:
																											field.fieldType !== 'text' ? 220 : undefined,
																									}}
																									type={
																										field.fieldType === 'number'
																											? 'number'
																											: field.fieldType === 'date'
																												? 'date'
																												: 'text'
																									}
																									value={editedValue ?? response?.value ?? ''}
																									onChange={(e) =>
																										handleFieldEdit(
																											section.id,
																											field.id,
																											e.target.value
																										)
																									}
																								/>
																							)}

																							{field.fieldType === 'select' && (
																								<TextField
																									select
																									size="small"
																									sx={{ minWidth: 220 }}
																									value={editedValue ?? response?.value ?? ''}
																									onChange={(e) =>
																										handleFieldEdit(
																											section.id,
																											field.id,
																											e.target.value
																										)
																									}>
																									<MenuItem value="">
																										<em>{field.placeholder || 'Select...'}</em>
																									</MenuItem>
																									{(field.options || []).map((opt, optIdx) => (
																										<MenuItem key={optIdx} value={opt}>
																											{opt}
																										</MenuItem>
																									))}
																								</TextField>
																							)}

																							{field.fieldType === 'yesno' && (
																								<Stack direction="row" spacing={0.5}>
																									<Chip
																										label={t('yes')}
																										size="small"
																										color={
																											(editedValue ?? response?.value) === 'yes'
																												? 'success'
																												: 'default'
																										}
																										variant={
																											(editedValue ?? response?.value) === 'yes'
																												? 'filled'
																												: 'outlined'
																										}
																										onClick={() =>
																											handleFieldEdit(section.id, field.id, 'yes')
																										}
																										sx={{
																											cursor: 'pointer',
																											height: 32,
																											fontSize: '1.25rem',
																											minWidth: 60,
																										}}
																									/>
																									<Chip
																										label={t('no')}
																										size="small"
																										color={
																											(editedValue ?? response?.value) === 'no'
																												? 'error'
																												: 'default'
																										}
																										variant={
																											(editedValue ?? response?.value) === 'no'
																												? 'filled'
																												: 'outlined'
																										}
																										onClick={() =>
																											handleFieldEdit(section.id, field.id, 'no')
																										}
																										sx={{
																											cursor: 'pointer',
																											height: 32,
																											fontSize: '1.25rem',
																											minWidth: 60,
																										}}
																									/>
																								</Stack>
																							)}

																							{field.fieldType === 'document' && (
																								<Box>
																									{effectiveDocId ? (
																										<Stack
																											direction="row"
																											alignItems="center"
																											spacing={1}
																											sx={{
																												bgcolor: alpha(COLORS.success.main, 0.06),
																												border: '1px solid',
																												borderColor: alpha(
																													COLORS.success.main,
																													0.3
																												),
																												borderRadius: 1,
																												px: 1.5,
																												py: 0.75,
																											}}>
																											<IconCheckCircle
																												sx={{
																													fontSize: 16,
																													color: 'success.main',
																												}}
																											/>
																											<Typography
																												sx={{
																													flex: 1,
																													fontWeight: 500,
																													fontSize: '1.25rem',
																													color: 'primary.main',
																													cursor: 'pointer',
																													'&:hover': {
																														textDecoration: 'underline',
																													},
																												}}
																												onClick={() =>
																													handleOpenDocViewer(
																														effectiveDocId,
																														effectiveDocName || ''
																													)
																												}>
																												{effectiveDocName || 'Document'}
																											</Typography>
																											<Tooltip title={t('removeAndReUpload')}>
																												<Button
																													size="small"
																													color="error"
																													onClick={() =>
																														handleFieldDocRemove(
																															section.id,
																															field.id
																														)
																													}
																													sx={{
																														minWidth: 'auto',
																														p: 0.25,
																													}}>
																													<IconClose
																														sx={{
																															fontSize: 16,
																														}}
																													/>
																												</Button>
																											</Tooltip>
																										</Stack>
																									) : (
																										<Button
																											component="label"
																											disabled={isUploadingThis}
																											size="small"
																											startIcon={
																												isUploadingThis ? (
																													<CircularProgress size={16} />
																												) : (
																													<IconDocument />
																												)
																											}
																											variant="outlined">
																											{isUploadingThis
																												? t('uploading')
																												: t('uploadFile')}
																											<input
																												hidden
																												type="file"
																												onChange={(e) =>
																													handleFieldDocUpload(
																														section.id,
																														field.id,
																														e
																													)
																												}
																											/>
																										</Button>
																									)}
																								</Box>
																							)}
																						</Box>
																					) : (
																						/* ===== READ-ONLY MODE ===== */
																						<DataRequestFieldRow
																							field={field}
																							response={response}
																							onDocClick={handleOpenDocViewer}
																							pl={4}
																						/>
																					)}
																					{/* Override comment */}
																					{isOverridden && response?.overrideComment && (
																						<Box
																							sx={{
																								pl: 6,
																								pr: 2,
																								pb: 0.5,
																							}}>
																							<Typography
																								sx={{
																									fontSize: '0.85rem',
																									color: COLORS.warning.dark,
																									fontStyle: 'italic',
																								}}>
																								Override: {response.overrideComment}
																							</Typography>
																						</Box>
																					)}
																				</Box>
																			);
																		})
																	)}
																</Box>

																{/* Save Changes button for edited rejected sections */}
																{canEditFields && sectionHasEdits(section.id) && (
																	<Box
																		sx={{
																			px: 2,
																			py: 1.5,
																			borderTop: '1px solid',
																			borderColor: alpha(COLORS.success.main, 0.2),
																			bgcolor: alpha(COLORS.success.main, 0.04),
																		}}>
																		<Stack
																			direction="row"
																			spacing={1.5}
																			justifyContent="flex-end"
																			alignItems="center">
																			<Typography
																				sx={{ flex: 1, fontSize: '1.25rem', color: '#6B7280' }}>
																				You have unsaved changes to this section
																			</Typography>
																			<Button
																				size="small"
																				color="inherit"
																				variant="outlined"
																				disabled={!!fieldSaveLoading}
																				onClick={() => handleCancelSectionEdits(section.id)}
																				sx={{ textTransform: 'none', fontWeight: 600 }}>
																				Cancel
																			</Button>
																			<Button
																				size="small"
																				color="primary"
																				variant="contained"
																				disabled={!!fieldSaveLoading}
																				startIcon={
																					fieldSaveLoading === section.id ? (
																						<CircularProgress size={16} />
																					) : (
																						<IconApprove />
																					)
																				}
																				onClick={() => handleSaveSectionFields(section.id)}
																				sx={{ textTransform: 'none', fontWeight: 600 }}>
																				Save
																			</Button>
																		</Stack>
																	</Box>
																)}

																{/* Section action buttons — only after rejected section is resubmitted */}
																{canAct &&
																	(!isRejected ||
																		sectionWasResubmitted ||
																		resubmittedSections[section.id]) && (
																		<Box
																			sx={{
																				px: 2,
																				py: 1.5,
																				borderTop: '1px solid',
																				borderColor: 'grey.100',
																				bgcolor: alpha(theme.palette.grey[50], 0.5),
																			}}>
																			<Stack
																				direction="row"
																				spacing={1.5}
																				justifyContent="flex-end">
																				<Button
																					size="small"
																					color="error"
																					variant="outlined"
																					disabled={sectionActionLoading !== null}
																					startIcon={
																						sectionActionLoading === section.id ? (
																							<CircularProgress size={16} />
																						) : (
																							<IconReject />
																						)
																					}
																					onClick={() => handleOpenSectionRejectDialog(section.id)}
																					sx={{ textTransform: 'none' }}>
																					{t('rejectSection')}
																				</Button>
																				<Button
																					size="small"
																					color="success"
																					variant="contained"
																					disabled={sectionActionLoading !== null}
																					startIcon={
																						sectionActionLoading === section.id ? (
																							<CircularProgress size={16} />
																						) : (
																							<IconApprove />
																						)
																					}
																					onClick={() => handleVerifySection(section.id)}
																					sx={{ textTransform: 'none' }}>
																					{t('verifySection')}
																				</Button>
																			</Stack>
																		</Box>
																	)}
															</Box>
														);
													}
												)}
											</Box>
										</CardDetail>
									)}

									{/* Sections pending notice before Payment Decision */}
									{voucher?.status === 'VERIFIED' &&
										!isCancelled &&
										mergedSections.length > 0 &&
										!allSectionsVerified && (
											<Box
												sx={{
													border: '1px solid',
													borderColor: alpha(COLORS.warning.main, 0.3),
													borderRadius: 2,
													bgcolor: alpha(COLORS.warning.main, 0.04),
													p: 2,
												}}>
												<Stack direction="row" alignItems="center" spacing={1.5}>
													<IconPending sx={{ color: COLORS.warning.main, fontSize: '1.5rem' }} />
													<Typography sx={{ fontWeight: 600, color: COLORS.warning.dark }}>
														{t('requirementsWarning')}
													</Typography>
												</Stack>
											</Box>
										)}

									{/* Payment Decision Card */}
									{showApprovalButtons && (
										<CardDetail
											icon={IconVerified}
											title={t('spendPlan.voucher.paymentDecision', 'Payment Decision')}
											variant="payment"
											themeColor={GRADIENTS.payment.icon}>
											<Box sx={{ p: 2.5 }}>
												<Typography color="text.secondary" sx={{ mb: 2.5 }} variant="body2">
													{t(
														'spendPlan.voucher.paymentDecisionDesc',
														'Review the verification responses above and make a decision on this payment request.'
													)}
												</Typography>
												<Stack direction="row" spacing={2}>
													<Button
														color="success"
														disabled={actionLoading}
														fullWidth
														onClick={handleApprovePayment}
														size="large"
														startIcon={
															actionLoading ? <CircularProgress size={20} /> : <IconCheckCircle />
														}
														sx={{
															boxShadow: `0 4px 12px ${alpha(COLORS.success.main, 0.3)}`,
															fontWeight: 700,
															py: 1.5,
															'&:hover': {
																boxShadow: `0 6px 16px ${alpha(COLORS.success.main, 0.4)}`,
															},
														}}
														variant="contained">
														{t('spendPlan.voucher.approvePayment', 'Approve Payment')}
													</Button>
													<Button
														color="error"
														disabled={actionLoading}
														fullWidth
														onClick={handleOpenRejectDialog}
														size="large"
														startIcon={<IconCancel />}
														sx={{
															fontWeight: 700,
															py: 1.5,
														}}
														variant="outlined">
														{t('spendPlan.voucher.rejectPayment', 'Reject Payment')}
													</Button>
												</Stack>
											</Box>
										</CardDetail>
									)}
								</Stack>
							</Grid>

							{/* Right Column: Linked Context + History */}
							<Grid item lg={4} xs={12}>
								<Stack spacing={2}>
									{/* NFT / Blockchain Card */}
									<CardDetail
										icon={IconToken}
										title={t('spendPlan.nftBackedVoucher', 'NFT-Backed Voucher')}
										variant="payment"
										themeColor={GRADIENTS.payment.icon}>
										<Box sx={{ p: 2 }}>
											{(() => {
												// Voucher lifecycle: GENERATED → VERIFIED → READY → REDEEMED
												// (CANCELLED short-circuits the flow). The on-chain mint
												// fires on the VERIFIED→READY transition; redemption fires
												// when an external payment confirmation flips the voucher
												// to REDEEMED. Anything earlier than READY hasn't touched
												// the chain yet — surface as neutral "awaiting" rows.
												const status = voucher.status;
												const isCancelled = status === 'CANCELLED';
												const isMintExpected = status === 'READY' || status === 'REDEEMED';
												// Redeem is a strict successor to mint — the SDK call
												// takes the NFT token ID, so even if the business status
												// has reached REDEEMED, we can't (and shouldn't) surface
												// a redeem failure or retry until the mint leg has
												// actually landed on chain. Gray it out until the
												// predecessor is green.
												const isRedeemExpected =
													status === 'REDEEMED' && !!voucher.mintTransactionHash;

												// Cancelled vouchers will never hit the chain — render a
												// flat neutral chip with no popover noise.
												if (isCancelled) {
													return (
														<Stack alignItems="center" spacing={1}>
															<IconToken
																sx={{
																	color: alpha(GRADIENTS.payment.icon, 0.4),
																	fontSize: 40,
																}}
															/>
															<OnChainStatusBadge
																notApplicable="This voucher was cancelled before any on-chain activity ran."
																notApplicableLabel={t('voucher.notOnChain')}
																compact
															/>
														</Stack>
													);
												}

												return (
													<Stack spacing={1.5}>
														<Stack alignItems="center" spacing={1.5}>
															<OnChainStatusBadge
																notApplicableLabel={t('voucher.awaitingApproval')}
																steps={[
																	{
																		key: 'mint',
																		label: 'Voucher NFT minted',
																		description:
																			'ERC-721 voucher minted from the spend-plan envelope. The NFT represents the recipient’s claim on the locked funds.',
																		status: !isMintExpected
																			? 'notApplicable'
																			: voucher.mintTransactionHash
																				? 'success'
																				: 'failed',
																		tooltip: !isMintExpected
																			? 'Minted when the voucher is approved for payment (READY).'
																			: voucher.mintTransactionHash
																				? voucher.nftTokenId
																					? `NFT #${voucher.nftTokenId}`
																					: 'Mint transaction recorded.'
																				: 'Mint did not complete on chain.',
																		txHash: voucher.mintTransactionHash || undefined,
																		errorMessage:
																			isMintExpected && !voucher.mintTransactionHash
																				? 'The mint call never landed on chain. Re-sync to retry — the voucher reference is idempotent.'
																				: undefined,
																		onRetry:
																			isMintExpected && !voucher.mintTransactionHash
																				? () => handleResyncVoucherBlockchain('mint')
																				: undefined,
																	},
																	{
																		key: 'redeem',
																		label: 'Voucher redeemed',
																		description:
																			'Burns the NFT and releases funds from the envelope. Fires when an external payment confirmation arrives for this voucher.',
																		status: !isRedeemExpected
																			? 'notApplicable'
																			: voucher.redemptionTransactionHash
																				? 'success'
																				: 'failed',
																		tooltip: !isRedeemExpected
																			? status === 'REDEEMED' && !voucher.mintTransactionHash
																				? 'Waiting on the mint step — redeem can only run after the NFT is on chain.'
																				: 'Redeemed when an external payment confirms this voucher.'
																			: voucher.redemptionTransactionHash
																				? 'Redemption transaction recorded.'
																				: 'Redemption did not complete on chain.',
																		txHash: voucher.redemptionTransactionHash || undefined,
																		errorMessage:
																			isRedeemExpected && !voucher.redemptionTransactionHash
																				? 'The redeem call never landed on chain. Re-sync to retry.'
																				: undefined,
																		onRetry:
																			isRedeemExpected && !voucher.redemptionTransactionHash
																				? () => handleResyncVoucherBlockchain('redeem')
																				: undefined,
																	},
																]}
																onRetry={() => handleResyncVoucherBlockchain()}
																compact
															/>
														</Stack>

														{voucher.nftTokenId && (
															<Paper
																elevation={0}
																sx={{
																	bgcolor: alpha(GRADIENTS.payment.icon, 0.06),
																	border: `1px solid ${alpha(GRADIENTS.payment.icon, 0.2)}`,
																	borderRadius: 1.5,
																	p: 1.5,
																}}>
																<Typography
																	color="text.secondary"
																	display="block"
																	sx={{ fontWeight: 600, letterSpacing: 0.4 }}
																	variant="caption">
																	{t('spendPlan.nftTokenId', 'NFT Token ID')}
																</Typography>
																<Typography
																	sx={{
																		color: GRADIENTS.payment.icon,
																		fontFamily: 'ui-monospace, SFMono-Regular, monospace',
																		fontSize: 13,
																		fontWeight: 600,
																		overflow: 'hidden',
																		textOverflow: 'ellipsis',
																		whiteSpace: 'nowrap',
																	}}>
																	#{voucher.nftTokenId}
																</Typography>
															</Paper>
														)}

														{voucher.envelopeId && (
															<Paper
																elevation={0}
																sx={{
																	bgcolor: alpha(GRADIENTS.payment.icon, 0.06),
																	border: `1px solid ${alpha(GRADIENTS.payment.icon, 0.2)}`,
																	borderRadius: 1.5,
																	p: 1.5,
																}}>
																<Typography
																	color="text.secondary"
																	display="block"
																	sx={{ fontWeight: 600, letterSpacing: 0.4 }}
																	variant="caption">
																	{t('spendPlan.envelopeIdLabel', 'Envelope')}
																</Typography>
																<Typography
																	sx={{
																		color: GRADIENTS.payment.icon,
																		fontFamily: 'ui-monospace, SFMono-Regular, monospace',
																		fontSize: 13,
																		fontWeight: 600,
																		overflow: 'hidden',
																		textOverflow: 'ellipsis',
																		whiteSpace: 'nowrap',
																	}}>
																	#{voucher.envelopeId}
																</Typography>
															</Paper>
														)}
													</Stack>
												);
											})()}
										</Box>
									</CardDetail>

									{/* Linked Context Card */}
									<CardDetail
										icon={IconLink}
										title={t('spendPlan.linkedTo', 'Linked To')}
										variant="payment"
										themeColor={GRADIENTS.payment.icon}>
										<Box sx={{ p: 2 }}>
											<Stack spacing={1.5}>
												{/* Work Plan */}
												{workPlan && (
													<Stack
														alignItems="center"
														direction="row"
														justifyContent="space-between"
														onClick={() => handleOpenWorkPlanPage(workPlan.id)}
														sx={{
															bgcolor: alpha(GRADIENTS.workPlan.icon, 0.04),
															border: '1px solid',
															borderColor: alpha(GRADIENTS.workPlan.icon, 0.2),
															borderRadius: 1.5,
															cursor: 'pointer',
															p: 1.5,
															transition: 'all 0.2s',
															'&:hover': {
																borderColor: GRADIENTS.workPlan.icon,
																boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
															},
														}}>
														<Stack alignItems="center" direction="row" spacing={1.5}>
															<Box
																sx={{
																	alignItems: 'center',
																	bgcolor: alpha(GRADIENTS.workPlan.icon, 0.12),
																	borderRadius: '50%',
																	display: 'flex',
																	height: { xs: 36, sm: 40 },
																	justifyContent: 'center',
																	width: { xs: 36, sm: 40 },
																}}>
																<IconWorkPlan
																	sx={{
																		color: GRADIENTS.workPlan.icon,
																		fontSize: { xs: 18, sm: 20 },
																	}}
																/>
															</Box>
															<Box>
																<Typography
																	color="text.secondary"
																	display="block"
																	variant="caption">
																	{t('spendPlan.workPlan')}
																</Typography>
																<Typography sx={{ fontWeight: 600 }} variant="body2">
																	{workPlan.name}
																</Typography>
															</Box>
														</Stack>
														<IconOpenInNew sx={{ color: GRADIENTS.workPlan.icon, fontSize: 16 }} />
													</Stack>
												)}

												{/* Activity */}
												{activity && (
													<Stack
														alignItems="center"
														direction="row"
														justifyContent="space-between"
														onClick={() => handleOpenActivityPage(activity.id)}
														sx={{
															bgcolor: alpha(GRADIENTS.activity.icon, 0.04),
															border: '1px solid',
															borderColor: alpha(GRADIENTS.activity.icon, 0.2),
															borderRadius: 1.5,
															cursor: 'pointer',
															p: 1.5,
															transition: 'all 0.2s',
															'&:hover': {
																borderColor: GRADIENTS.activity.icon,
																boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
															},
														}}>
														<Stack alignItems="center" direction="row" spacing={1.5}>
															<Box
																sx={{
																	alignItems: 'center',
																	bgcolor: alpha(GRADIENTS.activity.icon, 0.12),
																	borderRadius: '50%',
																	display: 'flex',
																	height: { xs: 36, sm: 40 },
																	justifyContent: 'center',
																	width: { xs: 36, sm: 40 },
																}}>
																<IconActivity
																	sx={{
																		color: GRADIENTS.activity.icon,
																		fontSize: { xs: 18, sm: 20 },
																	}}
																/>
															</Box>
															<Box>
																<Typography
																	color="text.secondary"
																	display="block"
																	variant="caption">
																	{t('spendPlan.activity')}
																</Typography>
																<Typography sx={{ fontWeight: 600 }} variant="body2">
																	{activity.name}
																</Typography>
															</Box>
														</Stack>
														<IconOpenInNew sx={{ color: GRADIENTS.activity.icon, fontSize: 16 }} />
													</Stack>
												)}

												{/* Contract */}
												{contract && (
													<Stack
														alignItems="center"
														direction="row"
														justifyContent="space-between"
														onClick={() => handleOpenContractPage(contract.id)}
														sx={{
															bgcolor: alpha(GRADIENTS.contract.icon, 0.04),
															border: '1px solid',
															borderColor: alpha(GRADIENTS.contract.icon, 0.2),
															borderRadius: 1.5,
															cursor: 'pointer',
															p: 1.5,
															transition: 'all 0.2s',
															'&:hover': {
																borderColor: GRADIENTS.contract.icon,
																boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
															},
														}}>
														<Stack alignItems="center" direction="row" spacing={1.5}>
															<Box
																sx={{
																	alignItems: 'center',
																	bgcolor: alpha(GRADIENTS.contract.icon, 0.12),
																	borderRadius: '50%',
																	display: 'flex',
																	height: { xs: 36, sm: 40 },
																	justifyContent: 'center',
																	width: { xs: 36, sm: 40 },
																}}>
																<IconContract
																	sx={{
																		color: GRADIENTS.contract.icon,
																		fontSize: { xs: 18, sm: 20 },
																	}}
																/>
															</Box>
															<Box>
																<Typography
																	color="text.secondary"
																	display="block"
																	variant="caption">
																	{t('spendPlan.contract')}
																</Typography>
																<Typography sx={{ fontWeight: 600 }} variant="body2">
																	{getContractNum(contract)} - {supplierName}
																</Typography>
															</Box>
														</Stack>
														<IconOpenInNew sx={{ color: GRADIENTS.contract.icon, fontSize: 16 }} />
													</Stack>
												)}
											</Stack>
										</Box>
									</CardDetail>
								</Stack>
							</Grid>
						</Grid>

						{/* Rejection Dialog */}
						{voucher && (
							<Dialog
								maxWidth="sm"
								fullWidth
								onClose={handleCloseRejectDialog}
								open={rejectDialogOpen}>
								<DialogTitle>{t('spendPlan.voucher.rejectPayment', 'Reject Payment')}</DialogTitle>
								<DialogContent>
									<Typography color="text.secondary" sx={{ mb: 2 }} variant="body2">
										{t(
											'spendPlan.voucher.rejectPaymentDesc',
											'Please provide a reason for rejecting this payment. This action cannot be undone.'
										)}
									</Typography>
									<TextField
										autoFocus
										fullWidth
										label={t('spendPlan.voucher.rejectionReason', 'Rejection Reason')}
										multiline
										onChange={(e) => setRejectionReason(e.target.value)}
										placeholder={t(
											'spendPlan.voucher.rejectionReasonPlaceholder',
											'Enter the reason for rejection...'
										)}
										required
										rows={4}
										value={rejectionReason}
									/>
								</DialogContent>
								<DialogActions>
									<Button disabled={actionLoading} onClick={handleCloseRejectDialog}>
										{t('cancel')}
									</Button>
									<Button
										color="error"
										disabled={!rejectionReason.trim() || actionLoading}
										onClick={handleRejectPayment}
										startIcon={actionLoading ? <CircularProgress size={16} /> : <IconReject />}
										variant="contained">
										{t('spendPlan.voucher.confirmReject', 'Confirm Rejection')}
									</Button>
								</DialogActions>
							</Dialog>
						)}

						{/* Section Rejection Dialog */}
						<Dialog
							maxWidth="sm"
							fullWidth
							onClose={() => setSectionRejectDialogOpen(false)}
							open={sectionRejectDialogOpen}>
							<DialogTitle>{t('rejectSectionTitle')}</DialogTitle>
							<DialogContent>
								<Typography color="text.secondary" sx={{ mb: 2 }} variant="body2">
									{t('rejectSectionBody')}
								</Typography>
								<TextField
									autoFocus
									fullWidth
									label={t('invoice.rejectionReason')}
									multiline
									onChange={(e) => setSectionRejectReason(e.target.value)}
									placeholder={t('invoice.rejectionReasonPlaceholder')}
									rows={3}
									value={sectionRejectReason}
								/>
							</DialogContent>
							<DialogActions>
								<Button
									disabled={sectionActionLoading !== null}
									onClick={() => setSectionRejectDialogOpen(false)}>
									{t('cancel')}
								</Button>
								<Button
									color="error"
									disabled={sectionActionLoading !== null}
									onClick={handleRejectSection}
									startIcon={
										sectionActionLoading !== null ? <CircularProgress size={16} /> : <IconReject />
									}
									variant="contained">
									{t('rejectSection')}
								</Button>
							</DialogActions>
						</Dialog>

						{/* Document Viewer Dialog */}
						{viewerDoc && (
							<DialogDocumentViewer
								currentIndex={0}
								documents={[viewerDoc]}
								blobFilenames={[viewerBlobFilename]}
								documentInfos={viewerDocInfo ? [viewerDocInfo] : undefined}
								isOpen={viewerOpen}
								onClose={() => setViewerOpen(false)}
								onNavigate={() => {
									/* single doc, no nav */
								}}
							/>
						)}
					</>
				) : null}
			</Box>

			<DialogAuditTimeline
				isOpen={auditTimelineOpen}
				onClose={() => setAuditTimelineOpen(false)}
				title={t('voucher.paymentAuditLog')}
				activities={voucherActivities}
				isLoading={auditLoading}
			/>

			<DialogMarkPaymentComplete
				voucher={
					voucher
						? {
								...voucher,
								supplierName,
								invoicePrefill: markPaidInvoice
									? {
											num: markPaidInvoice.num,
											date: markPaidInvoice.date,
											amount: markPaidInvoice.amount,
											currency: markPaidInvoice.currency,
										}
									: null,
							}
						: null
				}
				isOpen={markPaidOpen}
				onClose={() => setMarkPaidOpen(false)}
				onSuccess={() => {
					setMarkPaidOpen(false);
					refetch();
					refetchVouchers();
				}}
				projectId={project?.id}
			/>

			{/* Minting a voucher writes its NFT on-chain; show the on-chain animation
			    (same as v1) while the mint is in flight. */}
			<Dialog open={isMinting} maxWidth="xs" fullWidth>
				<DialogContent>
					<BlockchainProcessing status="LOADING" />
				</DialogContent>
			</Dialog>

			{voucher && (
				<DialogEntityDocuments
					isOpen={documentsDialogOpen}
					onClose={() => setDocumentsDialogOpen(false)}
					title={t('voucher.voucherDocuments')}
					sourceType="voucher"
					sourceId={voucher.id}
					projectId={project?.id}
					onViewDocument={(doc) => {
						handleOpenDocViewer(doc.id, doc.filename);
					}}
				/>
			)}
		</Box>
	);
};
